/**
 * English: the source dictionary. Swedish (sv.ts) is typed against it. A count is the `n` placeholder and has its `.one`
 * form beside it (the plural scan, src/__tests__/plurals.test.ts); a limit is `min` or `max` and never 1.
 */

export const en = {
  // ── The application ────────────────────────────────────────────────────────────────────────────────────────
  'app.name': 'Pascal',
  'app.tagline': 'The work board of the AI family',
  'demo.banner': 'Demonstration: a fictional organisation. Every person and name here is made up.',

  'common.loading': 'Loading…',
  'common.retry': 'Try again',
  'common.cancel': 'Cancel',
  'common.optional': 'optional',
  'common.fictional': 'fictional',
  'common.notYet': 'Not built yet',

  // ── The shell ──────────────────────────────────────────────────────────────────────────────────────────────
  'nav.main': 'Main navigation',
  'nav.skip': 'Skip to the content',
  'nav.board': 'Board',
  'nav.myWork': 'My work',
  'nav.units': 'Units',
  'nav.people': 'People',
  'nav.admin': 'Organisation',
  'nav.about': 'About',
  'nav.privacy': 'Privacy',
  'nav.signOut': 'Sign out',
  'nav.language': 'Language',
  'shell.signedInAs': 'Signed in as {name}',

  'role.admin': 'Administrator',
  'role.member': 'Member',
  'role.guest': 'Guest',
  'unit.kind.organisation': 'Organisation',
  'unit.kind.division': 'Division',
  'unit.kind.team': 'Team',
  'unit.role.lead': 'Lead',
  'unit.role.member': 'Member',

  // ── The family's words (contract explain.v1, appendix A) ───────────────────────────────────────────────────
  'explain.whatWentIn': 'What went in',
  'explain.howComputed': 'How was this computed?',

  // ── Sign-in and links ──────────────────────────────────────────────────────────────────────────────────────
  'signIn.title': 'Sign in',
  'signIn.email': 'Email address',
  'signIn.password': 'Password',
  'signIn.submit': 'Sign in',
  'signIn.busy': 'Signing in…',
  'signIn.invited': 'New here? Use the link in your invitation.',
  'signIn.forgot': 'Forgotten your password? Ask an administrator of your organisation to reset it.',

  'invite.noToken': 'This address needs the link from your invitation.',
  'invite.title': 'Create your account',
  'invite.resetTitle': 'Choose a new password',
  'invite.resetFor': 'A new password for {email}.',
  'invite.org': 'You are invited to {org} as {role}.',
  'invite.unit': 'Your team: {unit} ({role}).',
  'invite.by': 'Invited by {name}.',
  'invite.expires': 'The link works until {date}, once.',
  'invite.name': 'Your name',
  'invite.password': 'Password',
  'invite.passwordHint': '12 characters or more',
  'invite.terms': 'I have read the privacy notice.',
  'invite.create': 'Create account',
  'invite.resetSubmit': 'Set password',


  // ── The board (P1): projects, boards, cards, performers, the done rule, my work ─────────────────────────────
  'common.close': 'Close',
  'common.save': 'Save',
  'board.title': 'Boards',
  'board.intro': 'The projects you can see, each with its board. Open one to see who does what, and what is late.',
  'board.none': 'You can see no project yet. A project lead adds you, or a project is opened to the whole organisation. Nothing here does not mean nothing is going on.',
  'board.visibilityNote': 'You see a project when you are in it, when it is open to the organisation, or when you lead the unit that owns it or a unit above. Restricted projects are seen by their members only.',
  'board.all': 'All boards',
  'board.columns': 'Columns',
  'board.items': '{n} items',
  'board.items.one': '{n} item',
  'board.late': '{n} late',
  'board.late.one': '{n} late',
  'board.keys': 'Keyboard: Enter opens a card, M moves it, Space lifts it and the arrow keys carry it.',
  'board.viewerNote': 'You read this board as a viewer: you can open every card but change nothing.',
  'board.readOnlyNote': 'You see this board because of your place in the organisation, not as a member of the project, so you can read but not change it.',
  'board.guestNote': 'As a guest you move and change only the cards you perform or reported.',
  'view.label': 'View',
  'view.board': 'Board',
  'view.list': 'List',
  'project.new': 'New project',
  'project.name': 'Name',
  'project.key': 'Key',
  'project.keyHint': '2 to 6 capitals, for example AMLR: the cards are called AMLR-1, AMLR-2 …',
  'project.ownerUnit': 'Owned by',
  'project.visibility': 'Who sees it',
  'project.restrictedToggle': 'Restricted: suspicion-related or HR work',
  'project.restrictedHint': 'Only its members see it: no unit lead, no administrator. Never name a customer; use the case reference.',
  'project.defaultBoard': 'The project gets a board with the columns Backlog, To do, In progress, Review and Done. You become its lead.',
  'project.create': 'Create project',
  'project.restricted': 'Restricted',
  'project.open': '{n} open',
  'project.open.one': '{n} open',
  'project.blocked': '{n} blocked',
  'project.blocked.one': '{n} blocked',
  'project.done': '{n} done',
  'project.done.one': '{n} done',
  'project.itemsHidden': 'You see this project\'s name as an administrator; its cards are for its members.',
  'project.yourRole': 'You: {role}',
  'projectRole.lead': 'project lead',
  'projectRole.member': 'member',
  'projectRole.viewer': 'viewer',
  'visibility.members': 'Project members',
  'visibility.organisation': 'The whole organisation',
  'col.wipOf': '{n} of {max}',
  'col.wipOf.one': '{n} of {max}',
  'col.over': 'Over limit',
  'col.empty': 'No cards here.',
  'col.settings': 'Columns',
  'col.intro': 'Each column means one fixed stage (its category); counts and the done rule read the stage, never the name. The WIP limit is how many cards may be in the column before it says "Over limit".',
  'col.nameEn': 'Name in English',
  'col.nameSv': 'Name in Swedish',
  'col.wip': 'WIP limit',
  'col.category': 'Stage: {category}',
  'col.categoryLabel': 'Stage',
  'col.earlier': 'Move {name} left',
  'col.later': 'Move {name} right',
  'col.archive': 'Archive {name}',
  'col.archiveTitle': 'Archive the column {name}?',
  'col.archiveExplain': 'It leaves the board for good: an archived column cannot be brought back. It is empty, so no card moves.',
  'col.archiveConfirm': 'Archive',
  'col.new': 'New column',
  'col.add': 'Add column',
  'col.added': 'The column was added.',
  'col.saved': 'Saved.',
  'category.intake': 'Proposed',
  'category.backlog': 'Backlog',
  'category.ready': 'Ready',
  'category.active': 'In progress',
  'category.review': 'In review',
  'category.done': 'Done',
  'category.cancelled': 'Cancelled',
  'type.epic': 'Epic',
  'type.task': 'Task',
  'type.subtask': 'Subtask',
  'type.control': 'Control',
  'type.duty': 'Recurring duty',
  'type.remediation': 'Remediation',
  'type.change': 'Regulatory change',
  'type.request': 'Request',
  'type.milestone': 'Milestone',
  'priority.low': 'Low',
  'priority.normal': 'Normal',
  'priority.high': 'High',
  'priority.urgent': 'Urgent',
  'outcome.achieved': 'Achieved',
  'outcome.partly': 'Partly achieved',
  'outcome.not_achieved': 'Not achieved',
  'mode.person': 'People only',
  'mode.ai': 'AI expert only',
  'mode.pair': 'Pair (person and AI)',
  'mode.none': 'Nobody yet',
  'mode.short.person': 'Person',
  'mode.short.ai': 'AI',
  'mode.short.pair': 'Pair',
  'mode.short.none': 'Nobody',
  'ai.houseRule': 'AI proposes, a person confirms.',
  'ai.recordOnly': 'An AI expert on a card is a record of its part: it proposes, a person confirms. Pascal calls no model; the expert works in its own product, and its part is recorded here by hand.',
  'performer.title': 'Performers',
  'performer.list': 'Performed by {names}',
  'performer.aiNamed': '{name} (AI expert)',
  'performer.kind.person': 'Person',
  'performer.kind.ai_expert': 'AI expert',
  'performer.kindLabel': 'Who',
  'performer.person': 'Person',
  'performer.expert': 'AI expert',
  'performer.roleLabel': 'As',
  'performer.nobody': 'Nobody yet',
  'performer.noExpert': 'No AI expert',
  'performer.none': 'Nobody performs this card yet.',
  'performer.add': 'Add performer',
  'performer.remove': 'Remove {name}',
  'performerRole.lead': 'lead',
  'performerRole.assist': 'assists',
  'performerRole.reviewer': 'reviews',
  'lane.label': 'Lanes',
  'lane.none': 'No lanes',
  'lane.person': 'By person',
  'lane.mode': 'By who does it (person, AI, pair)',
  'lane.priority': 'By priority',
  'lane.noPerson': 'No person',
  'lane.named': 'Lane: {name}',
  'filter.label': 'Filter the cards',
  'filter.search': 'Search',
  'filter.person': 'Person',
  'filter.anyone': 'Anyone',
  'filter.mode': 'Who does it',
  'filter.anyMode': 'People, AI or pairs',
  'filter.labelName': 'Label',
  'filter.anyLabel': 'Any label',
  'filter.unit': 'Team',
  'filter.anyUnit': 'Any team',
  'filter.late': 'Only late',
  'filter.showing': 'Showing {shown} of {n} items',
  'filter.showing.one': 'Showing {shown} of {n} item',
  'card.label': '{code}: {title}',
  'dnd.card': 'card',
  'dnd.instructions': 'To move a card with the keyboard, press M and choose a column, or press Space to lift it, the left and right arrow keys to carry it, and Space to drop it. Escape cancels. Enter opens the card.',
  'dnd.picked': 'Lifted {code}.',
  'dnd.over': '{code} is over {column}.',
  'dnd.notOver': 'Not over a column.',
  'dnd.dropped': '{code} dropped on {column}.',
  'dnd.cancelled': 'Move cancelled.',
  'dnd.moved': '{code} moved to {column}.',
  'move.menu': 'Move {code} to',
  'move.to': 'Move to {column}',
  // Undo instead of "Are you sure?", and the status in the row (tests/acceptance/ui-undo.ts)
  'undo.undo': 'Undo',
  'undo.region': 'Last change',
  'undo.announce': '{message} To undo: Control+Z, or Undo in the notice at the bottom of the screen.',
  'undo.priority': '{code}: priority {priority}.',
  'undo.undoneMove': 'Undone: {code} is back in {column}.',
  'undo.undonePriority': 'Undone: {code} has priority {priority} again.',
  'reopen.title': 'Reopen {code}?',
  'reopen.explain': 'Its confirmation is cleared; the history keeps it. This cannot be undone: done again needs a person to confirm it with its outcome.',
  'reopen.explainSecond': 'The second person\'s check is cleared too, and a second person must check it again.',
  'reopen.confirm': 'Reopen',
  'reopen.bulkTitle': 'Reopen {n} done items?',
  'reopen.bulkTitle.one': 'Reopen {n} done item?',
  'reopen.bulkExplain': 'This reopens {n} done items and clears their confirmations; the history keeps them. This cannot be undone: done again needs a person to confirm each one with its outcome.',
  'reopen.bulkExplain.one': 'This reopens {n} done item and clears its confirmation; the history keeps it. This cannot be undone: done again needs a person to confirm it with its outcome.',
  'reopen.bulkList': 'The done items that reopen',
  'reopen.bulkSecond': 'Second checks are cleared too, and a second person must check those items again.',
  'reopen.bulkOthers': '{n} other selected items move too.',
  'reopen.bulkOthers.one': '{n} other selected item moves too.',
  'reopen.bulkConfirm': 'Reopen and move',
  'inline.status': 'Status of {code}: {column}. Change',
  'inline.statusMenu': 'Status of {code}',
  'inline.priority': 'Priority of {code}: {priority}. Change',
  'inline.priorityMenu': 'Priority of {code}',
  'errors.UNDO_GONE': 'This can no longer be undone: the time has passed, or the card has changed since.',
  'item.loading': 'Opening the card…',
  'item.new': 'New item',
  'item.create': 'Create item',
  'item.created': '{code} was created.',
  'item.title': 'Title',
  'item.description': 'Description',
  'item.descriptionHint': 'Never name a customer in a restricted case; use the case reference.',
  'item.type': 'Type',
  'item.column': 'Column',
  'item.priority': 'Priority',
  'item.dueOn': 'Due',
  'item.dueKind': 'Kind of date',
  'item.due': 'Due {date}',
  'item.dueHard': 'Deadline {date}',
  'item.noDue': 'No due date',
  'item.overdue': 'Late',
  'item.daysLate': '{n} days late',
  'item.daysLate.one': '{n} day late',
  'item.comments': '{n} comments',
  'item.comments.one': '{n} comment',
  'item.labels': 'Labels',
  'item.labelsHint': 'Separated by commas, at most 10',
  'item.noLabels': 'No labels',
  'item.owner': 'Accountable',
  'item.noOwner': 'Nobody named',
  'item.reportedBy': 'Reported by {name}',
  'item.state': 'Where it stands',
  'item.edit': 'Edit',
  'item.blocked': 'Blocked',
  'item.block': 'Mark as blocked',
  'item.unblock': 'No longer blocked',
  'item.blockedReason': 'Why is it blocked?',
  'item.fourEyes': 'Four eyes',
  'item.controlTask': 'Control task',
  'item.fourEyesRule': 'done only after a second person, who neither performed nor confirmed it, has checked it.',
  'item.fourEyesDefault': 'A control or remediation is a control task: a second person must check it before it is done.',
  'item.controlToggle': 'Control task (four eyes)',
  'item.controlStays': 'Four eyes stays on: only an administrator\'s decision with a reason turns it off, which is not offered yet.',
  'due.soft': 'Internal target',
  'due.hard': 'Legal or external deadline',
  'done.title': 'Done: {code}',
  'done.open': 'Done…',
  'done.outcome': 'How did it go?',
  'done.confirm': 'Confirm done',
  'done.youConfirm': 'You are recorded as the person who confirmed it.',
  'done.controlNote': 'This is a control task: a second person must confirm. It waits in review until then.',
  'done.confirmedBy': 'Confirmed by {name}: {outcome}.',
  'done.secondBy': 'Second check by {name}.',
  'done.waitingSecond': 'Waits for a second person',
  'done.announce': '{code} confirmed.',
  'second.title': 'Second check: {code}',
  'second.confirmedBy': '{name} confirmed it: {outcome}.',
  'second.explain': 'You check the work as the second person. You did not perform it and did not confirm it. After your check the card is done.',
  'second.record': 'Record the second check',
  'second.notYou': 'You performed or confirmed this card, so the second check is someone else\'s.',
  'second.notWriter': 'A member of the project who did not perform or confirm it makes the second check.',
  'contribution.title': 'What the AI experts did',
  'contribution.intro': 'Recorded by a person. A cost nobody reported stays empty.',
  'contribution.none': 'Nothing is recorded yet.',
  'contribution.model': 'Model: {model}',
  'contribution.noModel': 'Model not recorded',
  'contribution.noCost': 'Cost not reported',
  'contribution.cost': 'Cost US${cost}',
  'contribution.recordedBy': 'recorded by {name}, {date}',
  'contribution.expert': 'AI expert',
  'contribution.what': 'What it did',
  'contribution.use': 'How its output was used',
  'contribution.modelLabel': 'Model',
  'contribution.modelHint': 'If you know it, for example anthropic:claude-opus-5-5',
  'contribution.add': 'Record',
  'use.as_is': 'Used as it was',
  'use.light_edit': 'Lightly edited',
  'use.heavy_edit': 'Heavily edited',
  'use.discarded': 'Not used',
  'use.unknown': 'Use not recorded',
  'docs.title': 'Documents',
  'docs.intro': 'Links to where the documents live. Pascal stores no files.',
  'docs.none': 'No document is linked.',
  'docs.label': 'Name',
  'docs.url': 'Address (https)',
  'docs.urlPlaceholder': 'https://…',
  'docs.add': 'Add link',
  'docs.remove': 'Remove {label}',
  'links.title': 'Linked items',
  'links.none': 'No linked item.',
  'links.kind': 'Link',
  'links.code': 'Item code',
  'links.codePlaceholder': 'AMLR-12',
  'links.add': 'Link',
  'links.blocks.out': 'Blocks',
  'links.blocks.in': 'Blocked by',
  'links.relates.out': 'Relates to',
  'links.relates.in': 'Relates to',
  'links.duplicates.out': 'Duplicates',
  'links.duplicates.in': 'Duplicated by',
  'links.split_from.out': 'Split from',
  'links.split_from.in': 'Split into',
  'comments.title': 'Comments',
  'comments.none': 'No comments yet.',
  'comments.add': 'Comment',
  'comments.hint': 'About the work, never about a person.',
  'comments.send': 'Add comment',
  'history.title': 'History',
  'history.intro': 'Written by the database at every change; nobody can edit it.',
  'history.someone': 'Someone no longer listed',
  'history.bySystem': 'Pascal',
  'history.aPerson': 'a person',
  'history.oldColumn': 'a column no longer on the board',
  'history.created': 'Created in {column}',
  'history.moved': 'Moved from {from} to {to}',
  'history.reopened': 'Reopened, to {to}',
  'history.confirmed': 'Confirmed done: {outcome}',
  'history.secondPerson': 'Second check recorded (four eyes)',
  'history.assigned': '{name} added ({role})',
  'history.unassigned': '{name} removed',
  'history.blocked': 'Blocked: {reason}',
  'history.unblocked': 'No longer blocked',
  'history.commented': 'Comment added',
  'history.linkAdded': 'Link added',
  'history.contribution': 'AI contribution of {name} recorded',
  'history.fieldChanged': 'Changed: {fields}',
  'field.title': 'title',
  'field.description': 'description',
  'field.priority': 'priority',
  'field.labels': 'labels',
  'field.due_on': 'due date',
  'field.due_kind': 'kind of date',
  'field.owner_person_id': 'accountable person',
  'field.type': 'type',
  'field.control_task': 'four eyes',
  'field.start_on': 'start date',
  'field.parent_id': 'parent item',
  'list.caption': 'The cards of {name}',
  'list.code': 'Code',
  'list.title': 'Title',
  'list.column': 'Column',
  'list.type': 'Type',
  'list.priority': 'Priority',
  'list.due': 'Due',
  'list.none': 'No card matches the filter.',
  'bulk.selected': '{n} selected',
  'bulk.selected.one': '{n} selected',
  'bulk.all': 'Select every card shown',
  'bulk.pick': 'Select {code}',
  'bulk.choose': 'Choose…',
  'bulk.moveTo': 'Move to',
  'bulk.move': 'Move the selected',
  'bulk.priority': 'Priority',
  'bulk.setPriority': 'Set the priority',
  'bulk.doneNote': 'Done is confirmed card by card, with its outcome (open the card).',
  'bulk.result': 'Changed: {done} · refused: {refused}',
  'myWork.title': 'My work',
  'myWork.intro': 'Everything you perform or are accountable for, across your projects, by due date.',
  'myWork.count': '{n} items for you',
  'myWork.count.one': '{n} item for you',
  'myWork.late': 'Late',
  'myWork.week': 'Due in the next 7 days',
  'myWork.later': 'Later',
  'myWork.noDue': 'No due date',
  'myWork.done': 'Done in the last 14 days',
  'myWork.noneLate': 'Nothing of yours is late.',
  'myWork.noneHere': 'Nothing here.',
  'myWork.fourEyes': 'Waiting for your second check (four eyes)',
  'myWork.fourEyesIntro': 'Control tasks in your projects, confirmed by someone else, that you did not perform.',
  'myWork.what': 'Counted: the open cards where you are a performer or the accountable person, in the projects you can see.',
  'errors.FOUR_EYES': 'A second person who did not perform or confirm this item must make the check.',
  'errors.FOUR_EYES_PENDING': 'This is a control task: a second person must check it before it is done.',
  'errors.FOUR_EYES_NEEDS_CONFIRM': 'Confirm the item first; then a second person checks it.',
  'errors.FOUR_EYES_OFF': 'Four eyes stays on for this item.',
  'errors.NOT_CONTROL_TASK': 'Only a control task takes a second check.',
  'errors.DONE_NEEDS_OUTCOME': 'Say how it went before it is done.',
  'errors.DONE_NEEDS_CONFIRM': 'Done needs a person who confirms it.',
  'errors.GUEST_OWN_ITEMS': 'As a guest you change only the items you perform or reported.',
  'errors.PROJECT_READ_ONLY': 'You can read this project but not change it.',
  'errors.PROJECT_LEAD_ONLY': 'Only the project lead does this.',
  'errors.PROJECT_LAST_LEAD': 'A project keeps at least one lead.',
  'errors.PROJECT_KEY': 'A project key is 2 to 6 capitals, for example AMLR.',
  'errors.PROJECT_KEY_TAKEN': 'Another project has this key already.',
  'errors.PROJECT_OWNER_UNIT': 'A project is owned by a live unit.',
  'errors.ITEM_COLUMN': 'That column is not on this card\'s board.',
  'errors.ITEM_PARENT': 'A parent item is in the same project.',
  'errors.ITEM_CREATE_CLOSED': 'A card is created open and closed afterwards.',
  'errors.LABELS': 'A label is 1 to 40 characters, and a card has at most 10.',
  'errors.BLOCKED_REASON': 'Say why it is blocked.',
  'errors.COLUMN_NOT_EMPTY': 'Move the cards out of the column first.',
  'errors.PERFORMER_NOT_MEMBER': 'A person performs cards of a project they work in.',
  'errors.CONTRIBUTION_PERFORMER': 'A contribution belongs to an AI expert who performs this card.',
  'errors.LINK_CYCLE': 'That would make cards block each other in a circle.',
  'errors.URL_HTTPS': 'A link starts with https://.',
  'errors.BOARD_NO_REVIEW': 'This board has no review column.',
  'errors.BOARD_NO_DONE': 'This board has no done column.',

  // ── Units ──────────────────────────────────────────────────────────────────────────────────────────────────
  'units.title': 'Units',
  'units.intro': 'The organisation, its divisions and their teams. A division sits under the organisation; a team under a division.',
  'units.teams': '{n} teams',
  'units.teams.one': '{n} team',
  'units.new': 'New unit',
  'units.kind': 'Kind',
  'units.parent': 'Under',
  'units.name': 'Name',
  'units.code': 'Code',
  'units.codeHint': '2 to 8 capitals or digits, for example FCP',
  'units.create': 'Create unit',
  'units.created': 'The unit was created.',
  'units.members': 'Members',
  'units.noMembers': 'Nobody belongs to this unit yet.',
  'units.archived': 'Archived',
  'units.none': 'There are no divisions or teams yet.',
  'units.adminOnly': 'Only an administrator creates and changes units.',

  // ── People and invitations ─────────────────────────────────────────────────────────────────────────────────
  'people.title': 'People',
  'people.count': '{n} people',
  'people.count.one': '{n} person',
  'people.role': 'Role',
  'people.homeTeam': 'Home team',
  'people.homeTeamNone': 'No team',
  'people.disabled': 'Switched off',
  'people.invite': 'Invite a person',
  'people.email': 'Email address',
  'people.displayName': 'Name',
  'people.unitRole': 'In the team as',
  'people.inviteSend': 'Create invitation',
  'people.linkOnce': 'Send this link to the person. It is shown only now and works once.',
  'people.waitsSecond': 'A second administrator must confirm this invitation before a link exists.',

  'invites.title': 'Waiting invitations and resets',
  'invites.waiting': '{n} invitations waiting',
  'invites.waiting.one': '{n} invitation waiting',
  'invites.kind.invite': 'Invitation',
  'invites.kind.reset': 'Password reset',
  'invites.needsSecond': 'Waits for a second administrator',
  'invites.ready': 'Link given',
  'invites.confirm': 'Confirm',
  'invites.none': 'Nothing is waiting.',

  // ── About and privacy ──────────────────────────────────────────────────────────────────────────────────────
  'about.title': 'About Pascal',
  'about.createdBy': 'Created by Daniel Bardun.',
  'about.version': 'Version {version}',
  'about.family': 'Pascal is a member of the AI family: {names}.',
  'about.fonts': 'Fonts: Instrument Sans and Instrument Serif, under the SIL Open Font License 1.1.',
  'about.developed': 'Developed by Daniel Bardun, following the ANTON and Alexander frameworks and knowledge setup.',
  'about.licensedUnder': 'Licensed under the',
  'about.licenceName': 'European Union Public Licence (EUPL) v1.2',
  'privacy.title': 'Privacy at work',
  'privacy.draft': 'DRAFT, not yet reviewed by a lawyer.',
  'privacy.body': 'Pascal records the work, not the person. What it records about you, and who sees it, will be written out here in full before anyone uses it with real people.',
  'notFound.title': 'Not found',
  'notFound.body': 'There is nothing at this address, or nothing you may see.',

  // ── Errors (the server sends a code; the screen says it in the reader's language) ──────────────────────────
  'errors.INTERNAL': 'Something went wrong. Try again, and tell your administrator if it keeps happening.',
  'errors.UNREACHABLE': 'The server could not be reached. Check your connection and try again.',
  'errors.UNAVAILABLE': 'The server could not check your session. Try again in a moment.',
  'errors.NOT_FOUND': 'Not found.',
  'errors.FORBIDDEN': 'You may not do this.',
  'errors.ROLE_NEEDED': 'This needs another role.',
  'errors.CONFLICT': 'This exists already.',
  'errors.INVALID': 'That is not valid.',
  'errors.SAME_ORIGIN': 'The request was refused because it did not come from this site.',
  'errors.SIGN_IN': 'Sign in first.',
  'errors.SESSION_ENDED': 'Your session has ended. Sign in again.',
  'errors.SIGN_IN_INPUT': 'Enter your email address and password.',
  'errors.SIGN_IN_FAILED': 'That email address and password do not match an account.',
  'errors.LOCKED': 'Too many failed sign-ins for this account from your network. Try again in 15 minutes.',
  'errors.TOO_MANY': 'Too many sign-in attempts from this address. Try again later.',
  'errors.LINK_INVALID': 'This link is not valid, or it has expired or been used.',
  'errors.TEXT_REQUIRED': 'Fill in the text that is asked for.',
  'errors.TEXT_TOO_SHORT': 'Write at least {min} characters.',
  'errors.TEXT_TOO_LONG': 'Write at most {max} characters.',
  'errors.PASSWORD_MISSING': 'Choose a password.',
  'errors.PASSWORD_SHORT': 'Choose a password of at least {min} characters.',
  'errors.PASSWORD_LONG': 'That password is too long: at most 72 bytes.',
  'errors.UNIT_PARENT': 'A division goes under the organisation, and a team under a division.',
  'errors.UNIT_HAS_CHILDREN': 'Archive the units under this one first.',
  'errors.UNIT_MEMBER_UNIT': 'A person joins a live division or team.',
  'errors.ACCOUNT_EXISTS': 'An account with this email address exists already.',
  'errors.INVITE_EXISTS': 'An invitation for this email address is waiting already. Withdraw it first.',
  'errors.EMAIL_INVALID': 'That is not an email address.',
  'errors.HOME_UNIT_TEAM': 'A home team must be a live team.',
  'errors.CONFIRM_SECOND_ADMIN': 'A second administrator, not the one who asked, confirms this.',
  'errors.ADMIN_NEEDS_SECOND': 'An administrator comes through an invitation that a second administrator confirms.',
  'errors.TERMS_NEEDED': 'Confirm that you have read the privacy notice.',

  // ── P2: scrum and planning ─────────────────────────────────────────────────────────────────────────────────
  'nav.fourEyes': 'Four eyes',
  'view.backlog': 'Backlog',
  'view.reports': 'Reports',
  'lane.epic': 'By epic',
  'lane.noEpic': 'No epic',
  'filter.type': 'Type',
  'filter.anyType': 'Any type',
  'filter.epic': 'Epic',
  'filter.anyEpic': 'Any epic',
  'filter.sprint': 'Sprint',
  'filter.anySprint': 'Any sprint',
  'filter.noSprint': 'In no sprint (backlog)',
  'filters.saved.label': 'Saved filters',
  'filters.none': 'No saved filters yet',
  'filters.choose': 'Choose a saved filter',
  'filters.sharedName': '{name} (shared by {owner})',
  'filters.you': 'you',
  'filters.remove': 'Remove the filter "{name}"',
  'filters.removed': 'The filter "{name}" is removed.',
  'filters.saveThis': 'Save this filter',
  'filters.saved': 'The filter "{name}" is saved.',
  'filters.name': 'Name of the filter',
  'filters.share': 'Who sees it',
  'filters.shareMe': 'Only me',
  'filters.shareProject': 'Everyone in the project',
  'filters.typed': 'A saved filter keeps only the choices above. It never holds a search of its own.',
  'boardSettings.title': 'The board',
  'boardSettings.mode': 'How the board works',
  'boardSettings.modeHint': 'Scrum adds sprints: the board view then shows the running sprint.',
  'boardSettings.unit': 'Estimates in',
  'boardSettings.unitHint': 'Hours compare with the rough effort recorded at done; points are optional.',
  'boardSettings.saved': 'The board is saved.',
  'mode.board.kanban': 'Kanban',
  'mode.board.scrum': 'Scrum',
  'unit.hours': 'hours',
  'unit.points': 'points',
  'estimate.title': 'Estimate',
  'estimate.hours': '{n} hours',
  'estimate.hours.one': '1 hour',
  'estimate.points': '{n} points',
  'estimate.points.one': '1 point',
  'estimate.none': 'No estimate',
  'estimate.inHours': 'Estimate (hours)',
  'estimate.inPoints': 'Estimate (points)',
  'estimate.hint': 'Optional. Kept as the baseline when work starts.',
  'estimate.baseline': 'Baseline (frozen when work started)',
  'estimate.baselineIs': '{estimate} · {due} · kept {date}',
  'estimate.noBaseline': 'Not yet: the estimate and the due date are kept when work starts.',
  'estimate.reason': 'Why the estimate changes',
  'estimate.reasonHint': 'Describe the work, not a person. The first estimate stays as it was.',
  'estimate.save': 'Save the estimate',
  // The suggestion beside the estimate field (DESIGN.md §10.2 point 6; tests/acceptance/p4c-estimate-hint.ts)
  'estimate.calibLow': 'Your past estimates ran about {pct} % low; calibrated: {hours}',
  'estimate.calibHigh': 'Your past estimates ran about {pct} % high; calibrated: {hours}',
  'estimate.calibBasis': 'From {n} of your done items. Only you see this.',
  'estimate.calibBasis.one': 'From 1 of your done items. Only you see this.',
  'estimate.calibApply': 'Fill in {hours}',
  'estimate.calibFilled': 'Filled in, not saved. Change it if you like, then save.',
  'plan.title': 'Planning',
  'sprint.label': 'Sprint',
  'sprint.state.planned': 'Planned',
  'sprint.state.active': 'Running',
  'sprint.state.closed': 'Closed',
  'sprint.items': '{n} items',
  'sprint.items.one': '1 item',
  'sprint.noEstimate': '{n} items have no estimate',
  'sprint.noEstimate.one': '1 item has no estimate',
  'sprint.start': 'Start the sprint',
  'sprint.started': 'The sprint has started. Its commitment is kept as it is now.',
  'sprint.close': 'Close the sprint',
  'sprint.closeTitle': 'Close {name}',
  'sprint.closeOpen': '{n} items are not done. They go to the sprint you choose, or back to the backlog.',
  'sprint.closeOpen.one': '1 item is not done. It goes to the sprint you choose, or back to the backlog.',
  'sprint.closeNote': 'The done items stay in this sprint. Its record, the commitment and what was done, does not change after.',
  'sprint.leftoversTo': 'The open items go to',
  'sprint.startFreezes': 'Starting the sprint keeps its commitment: the open items in it and their estimates.',
  'sprint.committedLine': 'Committed at the start: {amount} in {n} items.',
  'sprint.committedLine.one': 'Committed at the start: {amount} in 1 item.',
  'sprint.empty': 'No item in this sprint yet. Plan items into it from the backlog below.',
  'sprint.new': 'New sprint',
  'sprint.name': 'Name',
  'sprint.defaultName': 'Sprint {n}',
  'sprint.defaultName.one': 'Sprint 1',
  'sprint.goal': 'Sprint goal',
  'sprint.goalHint': 'Optional: what the sprint is for, in a sentence.',
  'sprint.goalIs': 'Goal: {goal}',
  'sprint.startOn': 'Starts',
  'sprint.endOn': 'Ends',
  'sprint.lengthHint': 'At most six weeks. The start date becomes the day you start it.',
  'sprint.create': 'Create the sprint',
  'sprint.closedCount': '{n} closed sprints: see Reports.',
  'sprint.closedCount.one': '1 closed sprint: see Reports.',
  'sprint.planInto': 'Sprint',
  'sprint.planFor': 'Sprint for {code}',
  'sprint.running': 'Running: {name}, until {to}',
  'sprint.noneRunning': 'No sprint is running. Plan and start one in the backlog.',
  'sprint.showAll': 'Show every item, not only the sprint',
  'backlog.title': 'Backlog',
  'backlog.intro': 'The open items in no sprint, in the order of the backlog. The top comes first.',
  'backlog.empty': 'No open item outside a sprint. Nothing is counted here, so this does not mean "all done".',
  'epic.title': 'Epics',
  'epic.progress': '{done} of {n} items done',
  'epic.progress.one': '{done} of 1 item done',
  'epic.left': 'left: {amount}',
  'epic.tag': 'Epic {code}',
  'ready.label': 'Ready to start',
  'ready.yes': 'Ready: estimate and someone to do it',
  'ready.missing': 'Not ready: no {what}',
  'ready.estimate': 'estimate',
  'ready.performer': 'one doing it',
  'rank.group': 'Order of {code}',
  'rank.top': 'Move {code} to the top',
  'rank.up': 'Move {code} up',
  'rank.down': 'Move {code} down',
  'rank.bottom': 'Move {code} to the bottom',
  'rank.short.top': 'Top',
  'rank.short.up': 'Up',
  'rank.short.down': 'Down',
  'rank.short.bottom': 'Bottom',
  'rank.said.top': '{code} is at the top.',
  'rank.said.up': '{code} moved up.',
  'rank.said.down': '{code} moved down.',
  'rank.said.bottom': '{code} is at the bottom.',
  'rank.moved': '{code} moved.',
  'rank.draggable': 'item in the backlog',
  'rank.dragHint': 'Drag onto another item to put it above. With the keyboard, use the buttons.',
  'subitems.title': 'Sub-items',
  'subitems.parent': 'Under:',
  'subitems.noParent': 'nothing (a top-level item)',
  'subitems.none': 'No sub-items.',
  'subitems.count': '{n} sub-items',
  'subitems.count.one': '1 sub-item',
  'subitems.progress': '{done} of {n} sub-items done',
  'subitems.progress.one': '{done} of 1 sub-item done',
  'subitems.add': 'New {type} under this one',
  'subitems.create': 'Add',
  'subitems.moveUnder': 'Put under the item',
  'subitems.moveUnderHint': 'Its code, for example AMLR-4. Leave it empty to make this a top-level item.',
  'subitems.setParent': 'Move it there',
  'subitems.under': 'Sub-item of {code}:',
  'reports.intro': 'The board\'s figures, each from a versioned rule, with what it counted. None of them is about a person.',
  'reports.period': 'Done in',
  'reports.lastDays': 'the last {n} days',
  'reports.lastDays.one': 'the last day',
  'reports.periodLine': 'Done from {from} to {to}: {n} items.',
  'reports.periodLine.one': 'Done from {from} to {to}: 1 item.',
  'reports.sprint': 'Sprint',
  'reports.noneDone': 'nothing done in this period',
  'reports.noneDoneHonest': 'No item was done in this period, so there is nothing to judge. This is not "all on time".',
  'ontime.title': 'On time',
  'ontime.summary': 'On time: {onTime} of {n} items with a due date ({noDue} had none)',
  'ontime.summary.one': 'On time: {onTime} of 1 item with a due date ({noDue} had none)',
  'ontime.late': '{n} items late',
  'ontime.late.one': '1 item late',
  'ontime.early': '{n} items done before the due date (counted as on time)',
  'ontime.early.one': '1 item done before the due date (counted as on time)',
  'ontime.moved': '{n} items had their due date moved after work started; against the current date {onTime} of {withDue} were on time',
  'ontime.moved.one': '1 item had its due date moved after work started; against the current date {onTime} of {withDue} were on time',
  'ontime.noBaseline': '{n} items were done before Pascal kept baselines, and are not counted',
  'ontime.noBaseline.one': '1 item was done before Pascal kept baselines, and is not counted',
  'ontime.perPerson': 'There is no on-time figure per person here.',
  'ontime.item.onTime': 'Done on time (against the due date kept when work started).',
  'ontime.item.early': 'Done {n} days before the due date kept when work started.',
  'ontime.item.early.one': 'Done 1 day before the due date kept when work started.',
  'ontime.item.late': 'Done {n} days after the due date kept when work started.',
  'ontime.item.late.one': 'Done 1 day after the due date kept when work started.',
  'ontime.item.noDue': 'It had no due date when work started, so it is not judged.',
  'ontime.item.noBaseline': 'Done before Pascal kept baselines, so it is not judged.',
  'ontime.item.currentOnTime': 'Its due date moved later: against the current date it was on time.',
  'ontime.item.currentLate': 'Its due date moved: against the current date it was late.',
  'cycle.title': 'Flow times',
  'cycle.lead': 'From created to done',
  'cycle.cycle': 'From work started to done',
  'cycle.waiting': 'Waiting (in review or blocked)',
  'cycle.spread': 'median {median} days, 85 % within {p85} days ({n} items)',
  'cycle.spread.one': 'median {median} days, 85 % within {p85} days (1 item)',
  'cycle.never': '{n} items went to done without being in progress, and have no time from work started',
  'cycle.never.one': '1 item went to done without being in progress, and has no time from work started',
  'cycle.item': 'From created to done: {lead}. From work started: {cycle}. Waiting: {waiting}.',
  'cycle.notInProgress': 'never in progress',
  'cycle.noHistory': 'Its history does not show the way to done.',
  'days.count': '{n} days',
  'days.count.one': '1 day',
  'flow.title': 'How it went',
  'velocity.title': 'Velocity',
  'velocity.teamFigure': 'A team figure, never per person.',
  'velocity.none': 'No sprint is closed yet, so there is no velocity to show.',
  'velocity.chart': 'Committed and done per sprint, in {unit}',
  'velocity.committed': 'Committed at the start',
  'velocity.done': 'Done by the close',
  'velocity.mean': 'On average {mean} done per sprint, over {n} sprints.',
  'velocity.otherUnit': 'Closed sprints left out because they were counted in another unit: {n}.',
  'velocity.otherUnit.one': 'One closed sprint is left out because it was counted in another unit.',
  'velocity.mean.one': 'On average {mean} done per sprint, over 1 sprint.',
  'burn.sectionTitle': 'A sprint, day by day',
  'burn.title': 'Burndown',
  'burn.remaining': 'Remaining',
  'burn.ideal': 'Even pace to zero',
  'burn.start': 'Start',
  'burn.dates': '{from} to {to}',
  'burn.notStarted': 'This sprint has not started, so there is no line yet.',
  'burn.committed': 'Committed at the start: {amount}',
  'burn.added': 'Added after the start: {n} items, {amount}',
  'burn.added.one': 'Added after the start: 1 item, {amount}',
  'burn.removed': 'Taken out after the start: {n} items, {amount}',
  'burn.removed.one': 'Taken out after the start: 1 item, {amount}',
  'burn.reestimated': 'Change from re-estimates: {amount}',
  'burn.doneRemaining': 'Done: {done}. Remaining: {remaining}.',
  'burnup.title': 'Burnup',
  'burnup.done': 'Done',
  'burnup.scope': 'Scope (done and remaining)',
  'chart.table': 'Show as a table',
  'chart.seeTable': '{title}. The figures are in the table below the chart.',
  'chart.day': 'Day',
  'chart.sprint': 'Sprint',
  'chart.notYet': 'not yet',
  'rule.how': 'How was this computed?',
  'rule.ontime': 'A done item is on time when the day it was done is on or before the due date kept when work started (the baseline). Items with no due date then are counted apart and left out of the share; so are items done before baselines were kept. An item whose due date moved is also judged against the current date. Only items done in the chosen period count.',
  'rule.cycle': 'From each done item\'s history: from created to done, from the first time it was in progress to done, and the time it waited in review or blocked (each moment once). Days of 24 hours. The median is the middle value; 85 % of the items were within the second figure. Only items done in the chosen period count.',
  'rule.burn': 'From the commitment kept at the start and the history after it: for each day, the estimates of the sprint\'s open items (remaining) and of its done items (done). Items added or taken out after the start, and re-estimates, are counted apart. An item without an estimate counts zero and is counted as such. Days not reached yet are not drawn.',
  'rule.velocity': 'For each of the last six closed sprints: the estimates committed at the start, and the estimates of the items done by the close, in the board\'s unit. The mean is over the sprints shown. It describes the team\'s work, never a person.',
  'comments.edit': 'Edit',
  'comments.editLabel': 'Your comment',
  'comments.edited': 'edited',
  'comments.remove': 'Remove',
  'comments.removeConfirm': 'Remove your comment? It leaves the card; the audit trail keeps it.',
  'comments.removed': 'A comment removed by its author.',
  'history.baselineSet': 'Work started: the baseline was kept ({estimate}, {due})',
  'history.estimateChanged': 'Re-estimated from {from} to {to}',
  'history.sprintChanged': 'Moved from {from} to {to}',
  'history.commentEdited': 'Edited a comment',
  'history.commentRemoved': 'Removed a comment',
  'history.aSprint': 'a sprint',
  'history.reason': 'Why: {reason}',
  'item.fourEyesWaived': 'An administrator has turned four eyes off for this type: a new item of it needs no second person. You may still turn it on.',
  'fourEyes.title': 'Four eyes',
  'fourEyes.intro': 'Control and remediation tasks need a second person before they are done. Only an administrator, with a reason, turns that off for a type.',
  'fourEyes.now': 'Now',
  'fourEyes.onFor': 'On: a second person checks every new item of this type.',
  'fourEyes.offFor': 'Off since {date}, decided by {name}: {reason}',
  'fourEyes.newOnly': 'A decision applies to items created after it. An item that has four eyes keeps it.',
  'fourEyes.decide': 'Turn four eyes off for a type',
  'fourEyes.type': 'Type',
  'fourEyes.reason': 'Why',
  'fourEyes.reasonHint': 'At least {min} characters. Everyone in the organisation reads it.',
  'fourEyes.warning': 'Supervisors expect independent checks on control work. Record what checks the work instead.',
  'fourEyes.turnOff': 'Turn four eyes off',
  'fourEyes.decided': 'The decision is recorded.',
  'fourEyes.end': 'Turn four eyes on again for {type}',
  'fourEyes.endReason': 'Why (optional)',
  'fourEyes.ended': 'Four eyes is on again for new items.',
  'fourEyes.adminOnly': 'Only an administrator makes this decision.',
  'fourEyes.history': 'Earlier decisions',
  'fourEyes.past': '{type}: off from {from} to {to}. Decided by {name}, ended by {ender}: {reason}',
  'fourEyes.endedBecause': 'Ended because: {reason}',
  'errors.ESTIMATE_REASON': 'Work has started, so the first estimate is kept. Say why the estimate changes.',
  'errors.SPRINT_ACTIVE_EXISTS': 'This board has a running sprint. Close it before starting another.',
  'errors.SPRINT_EMPTY': 'Plan at least one open item into the sprint before starting it.',
  'errors.SPRINT_CLOSED': 'That sprint is closed. Choose an open sprint or the backlog.',
  'errors.SPRINT_RECORD': 'A closed sprint keeps the work done in it. Reopen the item first to plan it again.',
  'errors.SPRINT_BOARD': 'A sprint holds the items of its own board.',
  'errors.SPRINT_PLANNER': 'Sprints are planned by the project\'s leads and members.',
  'errors.SPRINT_LENGTH': 'A sprint ends on or after its start, within six weeks, and not in the past.',
  'errors.SPRINT_FROZEN': 'A running sprint keeps its dates and commitment, and a closed one does not change.',
  'errors.SPRINT_STATE': 'A sprint goes from planned to running to closed.',
  'errors.SPRINT_LEFTOVERS': 'The open items go to a planned sprint of this board, or back to the backlog.',
  'errors.FILTER_SHAPE': 'A saved filter holds only the board\'s filter choices.',
  'errors.FILTER_SHARE_WRITER': 'Only the project\'s leads and members share a filter with the project. Keep it for yourself.',
  'errors.COMMENT_AUTHOR_ONLY': 'Only the person who wrote a comment changes or removes it.',
  'errors.COMMENT_REMOVED': 'That comment has been removed.',
  'errors.FOUR_EYES_RULE_EXISTS': 'Four eyes is off for this type already.',
  'errors.ITEM_PARENT_CYCLE': 'An item cannot go under one of its own sub-items.',
  // ── P3: the learning fields ────────────────────────────────────────────────────────────────────────────
  'nav.learning': 'Learning',
  'nav.atWork': 'Pascal at work',
  'view.retro': 'Retrospective',
  'fun.title': 'How it felt to you',
  'fun.before.question': 'How engaging do you expect this work to feel to you?',
  'fun.after.question': 'How engaging did this work feel to you?',
  'fun.onlyYou': 'How did it feel to you? (only you see this)',
  'fun.1': 'Draining',
  'fun.2': 'Dull',
  'fun.3': 'Neutral',
  'fun.4': 'Enjoyable',
  'fun.5': 'Energising',
  'fun.skip': 'Skip',
  'fun.privateNote': 'Only you see your answers. Fun is about the work itself, not how well it went, and not about colleagues. Skip whenever you like.',
  'fun.sameAnswer': 'Your answer also counts in published group figures. If everyone in a group gives the same answer, the figure shows that answer.',
  'fun.whoSees': 'Who sees what',
  'fun.promptsOff': 'You have switched the fun question off. Switch it on again under My work.',
  'fun.saved': 'Your answer is saved. Only you see it.',
  'fun.removed': 'Your answer is deleted.',
  'fun.beforeLabel': 'Expected (before)',
  'fun.afterLabel': 'Felt (after)',
  'fun.small': 'Fewer than 5 people: not shown',
  'fun.smallMin': 'Fewer than {min} people: not shown',
  'fun.protect': 'Not shown, so that a smaller group cannot be worked out from the other figures.',
  'fun.noRatings': 'No answers yet.',
  'fun.mean': 'Mean {mean} of 5',
  'fun.people': '{n} people',
  'fun.people.one': '1 person',
  'fun.ratings': '{n} ratings',
  'fun.ratings.one': '1 rating',
  'fun.distribution': 'How the answers spread over the five points',
  'fun.aggregateTitle': 'How the work felt, as a group',
  'fun.gapUp': 'The work felt {gap} better than expected.',
  'fun.gapDown': 'The work felt {gap} worse than expected.',
  'fun.retroTitle': 'How the work felt to the team',
  'fun.aggregateNote': 'Shown only for at least 5 people and 5 answers. Nobody\'s own answer is ever shown here.',
  'fun.theSprint': 'The sprint\'s work',
  'fun.sprintNotClosed': 'How the work felt is shown once the sprint is closed, so that nobody can watch the figure change as one person answers.',
  'fun.byTeamElsewhere': 'On a kanban board, how the work felt is shown by team under Learning.',
  'fun.byTeamTitle': 'How the work felt, by team',
  'fun.byTeamIntro': 'Shown for periods that have ended. People count in their home team. A team smaller than 5 shows nothing on its own; its answers count in the division above.',
  'fun.unit': 'Team, division or organisation',
  'fun.period': 'Period',
  'fun.periodYear': 'The year {year}',
  'fun.periodQuarter': 'Quarter {q} of {year}',
  'fun.groupBy': 'Group',
  'fun.by.none': 'Everything together',
  'fun.by.type': 'By type of work',
  'fun.by.mode': 'By who did it: people, AI or a pair',
  'fun.everything': 'All the work',
  'radar.title': 'Difficulty radar',
  'radar.onCard': 'Difficulty rated',
  'radar.intro': 'Each axis from 0 none to 4 extreme. The radar describes the work, not the person. Skip any axis.',
  'radar.aboutItem': 'The radar describes the work, not the person: the project\'s members see it.',
  'radar.before': 'Before',
  'radar.after': 'After',
  'radar.beforeLegend': 'Difficulty before: how hard does this look now?',
  'radar.afterLegend': 'Difficulty after: how hard was it?',
  'radar.axis': 'Axis',
  'radar.skip': 'Skip',
  'radar.skipHint': 'Not rated.',
  'radar.skipped': 'not rated',
  'radar.none': 'Nobody has rated the difficulty yet.',
  'radar.chartTitle': 'Difficulty of {code}',
  'radar.rateBefore': 'Rate the difficulty',
  'radar.editBefore': 'Change the radar before',
  'radar.rateAfter': 'Rate the difficulty after',
  'radar.editAfter': 'Change the radar after',
  'radar.save': 'Save the radar',
  'radar.saved': 'The radar is saved.',
  'radar.profileTitle': 'Difficulty profile',
  'radar.profileNone': 'No done item in the period has a radar.',
  'radar.profileCounted': 'From {n} done items with a radar (after when rated, else before).',
  'radar.profileCounted.one': 'From 1 done item with a radar (after when rated, else before).',
  'radar.mean': 'Mean',
  'radar.gap': 'After minus before',
  'radar.meanOf': '{mean} ({n} items)',
  'radar.meanOf.one': '{mean} (1 item)',
  'radar.gapNote': 'The gap is over the {n} items rated both before and after. A positive figure: harder than it looked.',
  'radar.gapNote.one': 'The gap is over the 1 item rated both before and after. A positive figure: harder than it looked.',
  'radar.point.0': 'None',
  'radar.point.1': 'Low',
  'radar.point.2': 'Medium',
  'radar.point.3': 'High',
  'radar.point.4': 'Extreme',
  'radar.axis.knowledge': 'Knowledge',
  'radar.question.knowledge': 'How much domain or regulatory expertise does it need?',
  'radar.anchor.knowledge.0': 'No special knowledge: anyone in the team can do it.',
  'radar.anchor.knowledge.1': 'Basic knowledge of our own procedures.',
  'radar.anchor.knowledge.2': 'Working knowledge of the rules that apply.',
  'radar.anchor.knowledge.3': 'Specialist knowledge: one of our experts.',
  'radar.anchor.knowledge.4': 'Expertise we do not have in house, or a new law.',
  'radar.axis.tech': 'Tech',
  'radar.question.tech': 'How much technical or tool skill does it need?',
  'radar.anchor.tech.0': 'No tools beyond the usual office ones.',
  'radar.anchor.tech.1': 'A system we use every day.',
  'radar.anchor.tech.2': 'Some set-up, a data query or a spreadsheet model.',
  'radar.anchor.tech.3': 'Work in a system\'s settings or data, with IT.',
  'radar.anchor.tech.4': 'A new tool, an integration or code.',
  'radar.axis.invention': 'Invention',
  'radar.question.invention': 'How new is it to us? Is there a template or a past example?',
  'radar.anchor.invention.0': 'Done many times, with a template.',
  'radar.anchor.invention.1': 'Done before: an old example to follow.',
  'radar.anchor.invention.2': 'Partly new: some parts have no example.',
  'radar.anchor.invention.3': 'New to us, but done elsewhere.',
  'radar.anchor.invention.4': 'New to us and to the field: no example anywhere.',
  'radar.axis.business': 'Business',
  'radar.question.business': 'How much business judgement and impact is involved?',
  'radar.anchor.business.0': 'No business decision and no effect outside the team.',
  'radar.anchor.business.1': 'A small effect on one process.',
  'radar.anchor.business.2': 'Affects customers or another team.',
  'radar.anchor.business.3': 'A decision for management, with real costs.',
  'radar.anchor.business.4': 'Shapes the business: the board decides.',
  'radar.axis.uncertainty': 'Uncertainty',
  'radar.question.uncertainty': 'How clear is what "done" means at the start?',
  'radar.anchor.uncertainty.0': 'Done is clear and written down.',
  'radar.anchor.uncertainty.1': 'Mostly clear: a detail or two to settle.',
  'radar.anchor.uncertainty.2': 'The goal is clear, the way there is not.',
  'radar.anchor.uncertainty.3': 'The goal itself has to be worked out.',
  'radar.anchor.uncertainty.4': 'Nobody can say yet what done looks like.',
  'radar.axis.dependencies': 'Dependencies',
  'radar.question.dependencies': 'How much does it depend on other people, teams, suppliers or data?',
  'radar.anchor.dependencies.0': 'The team can do it alone.',
  'radar.anchor.dependencies.1': 'One input from another team.',
  'radar.anchor.dependencies.2': 'Several inputs, or one supplier.',
  'radar.anchor.dependencies.3': 'Waits on other teams or suppliers at several steps.',
  'radar.anchor.dependencies.4': 'Cannot move without parties we do not control.',
  'radar.axis.regulatory': 'Regulatory stakes',
  'radar.question.regulatory': 'What is the consequence of getting it wrong: a breach, a finding, a fine?',
  'radar.anchor.regulatory.0': 'No regulatory consequence.',
  'radar.anchor.regulatory.1': 'An internal finding at most.',
  'radar.anchor.regulatory.2': 'A finding by the auditors or the second line.',
  'radar.anchor.regulatory.3': 'A breach the supervisor would care about.',
  'radar.anchor.regulatory.4': 'A sanction, or harm to customers or the market.',
  'radar.axis.stakeholders': 'Stakeholder load',
  'radar.question.stakeholders': 'How many people must agree, and how hard are they to align?',
  'radar.anchor.stakeholders.0': 'Nobody else needs to agree.',
  'radar.anchor.stakeholders.1': 'One person signs off.',
  'radar.anchor.stakeholders.2': 'A few people, who mostly agree.',
  'radar.anchor.stakeholders.3': 'Many people, or a few who disagree.',
  'radar.anchor.stakeholders.4': 'Many parties with opposing interests.',
  'problems.title': 'Problem log',
  'problems.none': 'No problem recorded on this item.',
  'problems.noneInPeriod': 'No problem recorded in the period. That is not the same as none happening.',
  'problems.count': '{n} problems',
  'problems.count.one': '1 problem',
  'problems.byCategory': '{category}: {n}',
  'problems.byCategory.one': '{category}: 1',
  'problems.statusLine': 'Open: {open} · solved: {solved} · accepted: {accepted}',
  'problem.blameFree': 'Describe what happened to the work. Do not name or judge a person.',
  'problem.add': 'Record a problem',
  'problem.update': 'Update',
  'problem.save': 'Save the problem',
  'problem.saved': 'The problem is saved.',
  'problem.what': 'What happened',
  'problem.category': 'Kind of problem',
  'problem.howArose': 'How it arose',
  'problem.howFound': 'How it was found',
  'problem.timeLost': 'Time lost',
  'problem.timeLostNotSaid': 'Not said',
  'problem.status': 'Status',
  'problem.howSolved': 'How it was solved',
  'problem.solvedBy': 'Solved by',
  'problem.choose': 'Choose',
  'problem.prevent': 'What would prevent it next time?',
  'problem.foundOn': 'found {date}',
  'problem.howAroseIs': 'How it arose: {text}',
  'problem.howSolvedIs': 'Solved {by}: {text}',
  'problem.preventIs': 'Next time: {text}',
  'problem.cat.unclear_scope': 'Unclear scope',
  'problem.cat.missing_input': 'Missing input',
  'problem.cat.waiting_on_others': 'Waiting on others',
  'problem.cat.tool_or_tech': 'Tool or technology',
  'problem.cat.ai_output_wrong': 'AI output wrong',
  'problem.cat.knowledge_gap': 'Knowledge gap',
  'problem.cat.scope_change': 'Scope changed',
  'problem.cat.regulatory_change': 'Regulatory change',
  'problem.cat.data_quality': 'Data quality',
  'problem.cat.capacity': 'Capacity',
  'problem.cat.other': 'Other',
  'problem.found.self': 'By ourselves',
  'problem.found.four_eyes_review': 'In the four-eyes check',
  'problem.found.ai_check': 'By an AI check',
  'problem.found.stakeholder': 'By a stakeholder',
  'problem.found.audit_or_supervisor': 'By audit or the supervisor',
  'problem.found.monitoring': 'By monitoring',
  'problem.found.other': 'Another way',
  'problem.lost.lt_1h': 'Under an hour',
  'problem.lost.h1_4': '1 to 4 hours',
  'problem.lost.d05_1': 'Half a day to a day',
  'problem.lost.d1_3': '1 to 3 days',
  'problem.lost.gt_3d': 'More than 3 days',
  'problem.statusIs.open': 'Open',
  'problem.statusIs.solved': 'Solved',
  'problem.statusIs.accepted': 'Accepted (we live with it)',
  'problem.by.person': 'by a person',
  'problem.by.ai': 'by AI',
  'problem.by.pair': 'by a person and AI together',
  'problem.by.not_solved': 'not solved',
  'done.effort': 'Rough effort',
  'done.notTimesheet': 'A rough figure for learning. This is not a timesheet.',
  'done.yourEstimate': 'Your estimate: {n} hours.',
  'done.yourEstimate.one': 'Your estimate: 1 hour.',
  'done.yourEstimatePoints': 'Your estimate: {n} points.',
  'done.yourEstimatePoints.one': 'Your estimate: 1 point.',
  'done.noEstimate': 'There was no estimate.',
  'done.effortSkip': 'Skip',
  'done.effortUpTo': 'Up to {n} hours',
  'done.effortUpTo.one': 'Up to 1 hour',
  'done.effortOther': 'More, or another figure',
  'done.effortHours': 'Hours, roughly',
  'done.radar': 'Difficulty after (optional)',
  'done.radarFromBefore': 'Difficulty after, starting from before (optional)',
  'done.problems': 'Any problem worth recording?',
  'done.problemAdd': 'Add a problem',
  'done.problemRemove': 'Remove this problem',
  'done.aiHelp': 'AI help: how was it used?',
  'done.aiUsed': '{expert}: {what}',
  'done.aiNotSaid': 'Not said',
  'done.lesson': 'Lesson: what would we do differently?',
  'record.title': 'How it went: the learning record',
  'record.firstTimeRight': 'First time right',
  'record.ftrYes': 'Yes: it never went back from review or done.',
  'record.ftrNo': 'No: it went back {n} times.',
  'record.ftrNo.one': 'No: it went back once.',
  'record.actual': 'Rough effort',
  'record.aiShare': 'Share done with AI help',
  'record.lesson': 'Lesson',
  'record.quality': 'Quality note',
  'record.notSaid': 'Not recorded',
  'record.edit': 'Change the record',
  'record.save': 'Save',
  'record.saved': 'The learning record is saved.',
  'aiShare.none': 'None',
  'aiShare.some': 'Some',
  'aiShare.most': 'Most',
  'aiShare.all': 'All of it',
  'retro.intro': 'What the sprint (or the period) taught: problems, lessons, on time, first time right and AI use. A team\'s figures, never a person\'s.',
  'retro.sprint': 'Sprint',
  'retro.byPeriod': 'A period instead',
  'lessons.title': 'Lessons',
  'lessons.none': 'No lesson recorded.',
  'lessons.count': '{n} lessons',
  'lessons.count.one': '1 lesson',
  'rework.title': 'First time right',
  'rework.summary': 'First time right: {ftr} of {n} done items',
  'rework.summary.one': 'First time right: {ftr} of 1 done item',
  'rework.moves': 'Moves back from review or done: {n}',
  'rework.moves.one': 'Moves back from review or done: 1',
  'aiuse.title': 'AI help',
  'aiuse.none': 'No AI contribution recorded.',
  'aiuse.count': '{n} AI contributions recorded.',
  'aiuse.count.one': '1 AI contribution recorded.',
  'aiuse.use': '{use}: {n}',
  'aiuse.use.one': '{use}: 1',
  'aiuse.notSaid': 'How it was used not said: {n}',
  'aiuse.notSaid.one': 'How it was used not said: 1',
  'aiuse.cost': 'Cost reported: {usd} USD over {n} contributions.',
  'aiuse.cost.one': 'Cost reported: {usd} USD over 1 contribution.',
  'aiuse.costNotReported': 'Cost not reported for {n}.',
  'aiuse.costNotReported.one': 'Cost not reported for 1.',
  'completeness.title': 'What the done items recorded',
  'completeness.actual': 'Actual effort recorded on {k} of {n} done items',
  'completeness.actual.one': 'Actual effort recorded on {k} of 1 done item',
  'completeness.radar': 'Difficulty after rated on {k} of {n} done items',
  'completeness.radar.one': 'Difficulty after rated on {k} of 1 done item',
  'completeness.lesson': 'A lesson recorded on {k} of {n} done items',
  'completeness.lesson.one': 'A lesson recorded on {k} of 1 done item',
  'completeness.why': 'Every learning field is optional, so these say how much the figures above rest on.',
  'mix.title': 'Who did the work',
  'mix.line': '{mode}: {k} of {n} done items ({share} %)',
  'mix.line.one': '{mode}: {k} of 1 done item ({share} %)',
  'learning.title': 'Learning',
  'learning.intro': 'What the work teaches: on time, flow, first time right, problems, difficulty, who did it and how AI helped. With what each figure counted.',
  'learning.noRanking': 'No view ranks people. There is no performance score.',
  'learning.whoSeesWhat': 'What Pascal records about you, and who sees it',
  'learning.workTitle': 'The work in a project',
  'learning.project': 'Project',
  'learning.noProjects': 'There is no project whose items you can see.',
  'learning.doneInPeriod': '{n} items done in the period.',
  'learning.doneInPeriod.one': '1 item done in the period.',
  'rule.rework': 'From each done item\'s history: every move from review or done back to an earlier column category counts once (a reopening too); a move to cancelled does not. First time right is a done item with no such move. Only items done in the chosen period or sprint count.',
  'rule.problems': 'The problems recorded in the period (by the day they were found), counted by kind, time lost, status and who solved them. The texts are shown to the project\'s members; who recorded a problem is never shown.',
  'rule.radar': 'For each done item, the radar after when there is one, else the radar before; the mean per axis over the items that rated it. The gap is after minus before, over the items rated both times.',
  'rule.mix': 'Each done item\'s performers as they are now: people only, a pair (a person and an AI expert), an AI expert only (a person still confirmed), or nobody named. Shares in whole per cent.',
  'rule.aiuse': 'The AI contributions recorded on the items, counted by how a person said the output was used. The cost is the sum of the costs reported; a contribution without one is counted as "cost not reported", never as zero.',
  'rule.fun': 'The database counts the answers in a group and shows it only when at least 5 people and 5 answers are in it. When a group\'s figure, taken from a larger one, would leave fewer than 5 people, it is hidden too. People count in their home team; a person who has left is not counted. The mean is over the five points.',
  'you.title': 'You',
  'you.onlyYou': 'Only you see this',
  'you.funTitle': 'How your work felt',
  'you.noRatings': 'You have not answered the fun question yet.',
  'you.before': 'Expected: mean {mean} of 5 over {n} answers',
  'you.before.one': 'Expected: mean {mean} of 5 over 1 answer',
  'you.after': 'Felt: mean {mean} of 5 over {n} answers',
  'you.after.one': 'Felt: mean {mean} of 5 over 1 answer',
  'you.beforeNone': 'Expected: no answer yet',
  'you.afterNone': 'Felt: no answer yet',
  'you.gapUp': 'Your work felt {gap} better than you expected ({n} items).',
  'you.gapUp.one': 'Your work felt {gap} better than you expected (1 item).',
  'you.gapDown': 'Your work felt {gap} worse than you expected ({n} items).',
  'you.gapDown.one': 'Your work felt {gap} worse than you expected (1 item).',
  'you.mostEnergising': 'Your most energising kind of work: {type}',
  'you.ontimeTitle': 'Your work on time',
  'you.noneDone': 'You have not finished an item in the last 12 months.',
  'you.ontimeOnlyYou': 'The items you performed in the last 12 months. Only you see this figure; no manager has a view of it.',
  'you.prompts': 'The fun question',
  'you.promptsOn': 'Pascal asks how you expect work to feel, and how it felt. You can always skip.',
  'you.promptsOffNow': 'Switched off: Pascal does not ask you.',
  'you.turnOff': 'Stop asking me',
  'you.turnOn': 'Ask me again',
  'you.saved': 'Saved.',
  'you.ratingsTitle': 'Your answers ({n})',
  'you.ratingsTitle.one': 'Your answer (1)',
  'you.itemGone': 'An item you no longer see',
  'you.remove': 'Delete',
  'you.showAll': 'Show all {n}',
  'you.showAll.one': 'Show the 1 answer',
  'you.showFewer': 'Show fewer',
  'you.eraseAll': 'Delete all my answers',
  'you.eraseAllConfirm': 'Delete every answer you have given? This cannot be undone.',
  'you.eraseAllYes': 'Yes, delete them',
  'you.erasedAll': 'All your answers are deleted.',
  'you.retention': 'Your answers are kept for 24 months, then deleted. You may delete them at any time.',
  'atWork.title': 'Pascal at work: what it records about you',
  'atWork.intro': 'Pascal records how work goes so that teams can learn. It is about the work, not about you as a person. This page says what is recorded, who sees it, and what you can choose.',
  'atWork.records.title': 'What is recorded',
  'atWork.records.items': 'The work items you perform, own, report, confirm or check, with their dates and history.',
  'atWork.records.fun': 'If you answer: how engaging you expected a piece of work to feel, and how it felt, on a scale from 1 to 5. Every answer is optional.',
  'atWork.records.radar': 'The difficulty of an item on eight axes. It describes the work, not the person.',
  'atWork.records.problems': 'Problems with the work: what happened, how it was found and solved. Blame-free: nobody is named on a problem.',
  'atWork.records.effort': 'A rough figure for the effort an item took, if someone gives one. It is not a timesheet and is never used for pay or invoicing.',
  'atWork.records.ai': 'Which AI expert helped and how its output was used.',
  'atWork.whoSees.title': 'Who sees what',
  'atWork.col.what': 'What',
  'atWork.col.you': 'You',
  'atWork.col.members': 'The project\'s members',
  'atWork.col.leads': 'Unit leads',
  'atWork.col.admins': 'Administrators',
  'atWork.row.ownFun': 'Your own fun answers',
  'atWork.row.funGroups': 'Fun answers of a group',
  'atWork.row.ownOntime': 'Whether your own items were on time',
  'atWork.row.items': 'Items, performers, outcome, radar',
  'atWork.row.problems': 'Problem texts',
  'atWork.row.effort': 'Rough effort',
  'atWork.yes': 'Yes',
  'atWork.no': 'No',
  'atWork.group5': 'Only for at least 5 people',
  'atWork.ifVisible': 'If they can see the project',
  'atWork.metadata': 'The project\'s name and counts; the items only if they can see the project',
  'atWork.yesNoName': 'Yes, without who recorded them',
  'atWork.never.title': 'What Pascal never does',
  'atWork.never.score': 'There is no performance score for a person, and no switch to turn one on.',
  'atWork.never.rank': 'No view ranks people. There is no "top performer".',
  'atWork.never.ai': 'No AI evaluates you, proposes who should do work from your history, or decides about you.',
  'atWork.never.timesheet': 'Pascal is not time reporting: no clock, no time per person in a status.',
  'atWork.never.small': 'Nobody sees a group\'s fun answers when the group has fewer than 5 people, or when a smaller group could be worked out from the figures.',
  'atWork.choices.title': 'What you can choose',
  'atWork.choices.skip': 'Skip any question: only the outcome of a done item is required.',
  'atWork.choices.off': 'Switch the fun question off, under My work.',
  'atWork.choices.delete': 'Delete your fun answers, one or all, at any time, under My work.',
  'atWork.choices.export': 'Download all your data: this comes with a later version of Pascal.',
  'atWork.retention.title': 'How long it is kept',
  'atWork.retention.fun': 'Fun answers: 24 months after the answer, then deleted.',
  'atWork.retention.leaver': 'When you leave: your account is switched off, and your fun answers are deleted 30 days later.',
  'atWork.retention.work': 'Work records (items, problems, radar): while the project exists and 5 years after it closes, as business records.',
  'atWork.legal.title': 'The legal frame, in short',
  'atWork.draft': 'Draft: needs legal review.',
  'atWork.legal.gdpr': 'GDPR applies: your employer is the controller of this data, and the provider of Pascal processes it on their behalf.',
  'atWork.legal.basis': 'The legal basis is your employer\'s legitimate interest in planning and following up work, not your consent; the fun question stays voluntary.',
  'atWork.legal.dpia': 'Your employer makes a data protection impact assessment before using Pascal with real people.',
  'atWork.legal.mbl': 'An employer bound by a collective agreement negotiates with the union before introducing Pascal (MBL section 11) and keeps it informed (section 19).',
  'atWork.legal.aiAct': 'Pascal\'s AI does not evaluate people or allocate work from their behaviour, which keeps it outside the EU AI Act\'s high-risk use for work.',
  'atWork.yourPanel': 'Your own figures and answers are in the "You" panel on',
  'errors.FUN_NOT_YOUR_WORK': 'Only the people who do, own or confirm this item rate how it felt.',
  'errors.FUN_AFTER_NEEDS_CONFIRM': 'How it felt afterwards is asked once the item is confirmed.',
  'errors.FUN_BEFORE_NEEDS_OPEN': 'How you expect it to feel is asked while the work is open.',
  'errors.RADAR_AFTER_NEEDS_CONFIRM': 'The radar after is rated once the item is confirmed.',
  'errors.RADAR_EMPTY': 'Rate at least one axis, or close without saving.',
  'errors.PROBLEM_HOW_SOLVED': 'Say how the problem was solved, and by whom: a person, AI or both.',
  'errors.PROBLEM_FOUND_ON': 'A problem is found today or earlier.',
  'errors.FUN_AGGREGATE_ROLE': 'Group figures are for the organisation\'s members.',
  'errors.FUN_AGGREGATE_PERIOD': 'Choose a calendar year or quarter.',
  'errors.FUN_AGGREGATE_SHAPE': 'Choose a team and how to group.',
  'errors.FUN_AGGREGATE_OPEN': 'Figures are shown for a period that has ended, or a sprint that is closed.',
  // ── P4a: the layers and the published figures ─────────────────────────────────────────────────────────────
  'nav.layers': 'Layers',
  'layers.title': 'Layers: the organisation, its divisions and teams',
  'layers.intro': 'Two ways to add up the work. Done by people here: the work this unit\'s people perform, in any project. Work in projects owned here: the work in this unit\'s projects, whoever does it. They differ when people help other units.',
  'layers.counted': 'Counted: every open item, and every item done from {from} to {to}, in the projects you can see.',
  'layers.up': 'Up to {name}',
  'layers.open': 'Open this layer',
  'layers.orgPath': 'Done by people here',
  'layers.orgPathNote': 'Work that people whose home team is here perform, in any project. An item done by people of two teams counts in each, so the teams do not add up to their division.',
  'layers.projectPath': 'Work in projects owned here',
  'layers.projectPathNote': 'Work in the projects this unit, or a unit under it, owns, whoever does it.',
  'layers.unseen': '{n} projects you cannot see',
  'layers.unseen.one': '1 project you cannot see',
  'layers.unseenRestricted': '{n} of them restricted',
  'layers.unseenRestricted.one': '1 of them restricted',
  'layers.unseenCounts': 'open {open}, late {late}, done in the period {done}. Not in the figures above; no name is shown.',
  'layers.smallMin': 'Fewer than {min} people: not shown',
  'layers.protect': 'Not shown, so that a smaller group cannot be worked out from the other figures.',
  'layers.nothing': 'No open or recently done work is counted here.',
  'layers.fig.open': 'Open',
  'layers.fig.blocked': 'Blocked',
  'layers.fig.late': 'Late (open past the due date)',
  'layers.fig.done': 'Done in the period',
  'layers.fig.throughput': 'Done per week',
  'layers.fig.ontime': 'On time',
  'layers.fig.people': 'People',
  'layers.ontimeOf': '{on} of {of} done items with a due date ({none} without one)',
  'layers.ontimeNone': 'None of the {n} done items had a due date',
  'layers.ontimeNone.one': 'The 1 done item had no due date',
  'layers.peopleHere': '{n} people of this unit',
  'layers.peopleHere.one': '1 person of this unit',
  'layers.peopleAny': '{n} people, from any unit',
  'layers.peopleAny.one': '1 person, from any unit',
  'layers.plannedTitle': 'Planned next',
  'layers.planned': 'Planned',
  'layers.plan.timeline': 'Timeline with dependencies',
  'layers.plan.roadmap': 'Roadmap by quarter',
  'layers.plan.workload': 'Workload against capacity',
  'layers.plan.calibration': 'Estimate calibration',
  'layers.plan.learningFull': 'The full Learning view: trends and the effect of AI help',
  'rule.rollup': 'Counted: every open item and every item done in the chosen period, in the projects you can see. Done by people here: an item counts when a person whose home team is in this unit performs it; shown only when at least 5 such people did the counted work, and hidden too when a smaller group could be worked out from the figures beside it. Work in projects owned here: the items of the projects this unit or a unit under it owns. Late: open with a due date before today. On time as in ontime.v1. Done per week: done in the period, divided by its weeks.',
  'rule.funPublished': 'Once a quarter or a year has ended, or a sprint is closed, its figures are worked out once, for the whole organisation, and kept: a changed or deleted answer, someone leaving or changing team, or an item retyped later changes nothing shown. A group shows only with at least 5 people and 5 answers; a figure that would let a smaller group be worked out is hidden too. A year is the sum of its four quarters and shows only when each of them does; its people are counted per quarter. The mean is over the five points.',
  'fun.yearPeople': 'People are counted per quarter',
  'fun.unpublished': 'Not published: this unit did not exist when the figures for the period were published.',
  'errors.LAYERS_ROLE': 'Layers are for the organisation\'s members.',
  'atWork.figures.title': 'Figures about groups',
  'atWork.figures.published': 'A group\'s figures for a period that has ended are worked out once and kept. If you later change an answer, or leave, the figure already published stays as it was. If you delete an answer, every published figure that counted it is worked out again without it, and the old figure is withdrawn.',
  'atWork.figures.layers': 'The layers add up the work of a team or a division. Work done by a unit\'s people is shown only for at least 5 people; there is no figure for one person.',
  'atWork.figures.limits': 'What the rules cannot rule out: figures that cut across several groupings at once (for example teams, types of work and sprints together) are not proved safe from every subtraction. If everyone in a group gave the same answer, the group\'s figure shows that answer.',
  // P4a-2: goals, the Heading view, the dashboards
  'nav.heading': 'Heading',
  'nav.dashboards': 'Dashboards',
  'layers.toHeading': 'Heading: goals and the work not linked to any goal',
  'layers.toDashboards': 'Dashboards for every layer',
  'layers.plan.calendar': 'Calendar of due dates, milestones and sprints',
  'layers.plan.ics': 'A calendar feed (ICS) for your own items',
  'heading.title': 'Heading: where we are going',
  'heading.intro': 'The goals of the organisation, its divisions and teams for a period, how far they have come, and the work that serves them. Progress is worked out from the key results; no model decides it.',
  'heading.period': 'Period',
  'heading.tree': 'Goals for {period}',
  'heading.goalsNone': 'No goals for {period} yet.',
  'heading.lines': 'Two honest lines',
  'heading.drift': 'Work not linked to any goal: {unlinked} of {done} items done in {period} ({percent} %)',
  'heading.driftNone': 'Work not linked to any goal: no item was done in {period} in the projects you can see.',
  'heading.noWork': 'Goals with no work linked: {without} of {of}',
  'heading.counted': 'Counted: the items done in the period in the projects you can see, up to today; an item counts as linked when it, an item above it or its project is linked to a goal that is active or done (not a draft, not dropped). Goals with no work linked: of the goals shown that are not dropped, also counting linked work in projects you cannot see.',
  'heading.newGoal': 'New goal',
  'heading.toDashboards': 'The dashboards per layer',
  'goal.status.draft': 'Draft',
  'goal.status.active': 'Active',
  'goal.status.done': 'Done',
  'goal.status.dropped': 'Dropped',
  'goal.statusLabel': 'Status',
  'goal.owner': 'Owner: {name}',
  'goal.ownerLabel': 'Owner (optional)',
  'goal.noOwner': 'No owner named',
  'goal.noKr': 'No key results yet: no progress is shown.',
  'goal.noProgress': 'No progress yet: none of its {of} key results has a value.',
  'goal.progress': '{percent} % on the way, from {from} of {of} key results',
  'goal.keyResults': 'Key results',
  'goal.work': 'Linked work you can see: {open} open, {done} done, {late} late.',
  'goal.noWork': 'No work linked yet.',
  'goal.unseen': '{n} more linked items in projects you cannot see',
  'goal.unseen.one': '1 more linked item in a project you cannot see',
  'goal.unseenCounts': '{open} open, {done} done, {late} late (counted, never named)',
  'goal.trend': 'Linked items done per quarter:',
  'goal.change': 'Change the status',
  'goal.save': 'Save',
  'goal.saved': 'Saved.',
  'goal.addKr': 'Add a key result',
  'goal.krAdded': 'The key result is added.',
  'goal.krTitle': 'What is measured',
  'goal.krKind': 'How it is measured',
  'goal.krBaseline': 'Where it starts (baseline)',
  'goal.krTarget': 'Target',
  'goal.krTargetItems': 'Optional: how many linked items are to be done. Left empty, every linked item counts.',
  'goal.krUnit': 'Unit (optional)',
  'goal.enter': 'Enter a value',
  'goal.enterOne': 'Enter',
  'goal.entered': 'The value is entered.',
  'goal.enterNote': 'A value is kept with your name and today\'s date; an earlier value is never changed.',
  'goal.link': 'Link work',
  'goal.linkProject': 'A project',
  'goal.linkProjectHint': 'Every item of the project counts. A project is linked by its lead.',
  'goal.linkItem': 'An item, by its code',
  'goal.linkItemHint': 'The item and the items under it count. Linked by whoever may change the item.',
  'goal.linkOne': 'Link',
  'goal.linked': 'Linked.',
  'goal.choose': 'Choose',
  'goal.created': 'The goal is set.',
  'goal.unit': 'Whose goal',
  'goal.parent': 'Serves the goal (optional)',
  'goal.parentHint': 'A goal of the same unit or a unit above, whose period holds this one.',
  'goal.noParent': 'None: a goal of its own',
  'goal.title': 'The goal',
  'goal.titleHint': 'Every member of the organisation reads the goals and their values: do not name customers or cases, and do not measure one person.',
  'atWork.goals.title': 'Goals',
  'atWork.goals.who': 'Every member of the organisation reads the goals, who owns each, and who entered each value of a key result, with the day. The work linked to a goal is counted from the items each reader may see; work in projects you cannot see counts only as a number.',
  'atWork.goals.noScore': 'The progress of a goal is about its work, not a score of its owner or of anyone who did the work. A goal is never set to measure one person; there is no view of goals by person, except your own dashboard.',
  'goal.why': 'Why (optional)',
  'goal.whoSets': 'A unit\'s goals are set by its leads, the leads above it and administrators.',
  'kr.kind.items_done': 'items done',
  'kr.kind.metric': 'a number',
  'kr.kind.milestone': 'a milestone',
  'kr.items': '{current} of {of} linked items done',
  'kr.noItems': 'No linked items yet',
  'kr.metric': 'Now {current} {unit} (from {baseline}, target {target})',
  'kr.metricRange': 'from {baseline}, target {target} {unit}',
  'kr.noValue': 'No value entered yet',
  'kr.reached': 'Reached',
  'kr.notReached': 'Not reached yet',
  'kr.lastEntry': 'Last value {value}, for {date}, entered by {name}',
  'rule.kr': 'Items done: the linked items done (whenever they were done), of the target when one is set, else of every linked item that is not cancelled (also those in projects you cannot see, as counts); at most 100 %. A number: how far the latest value has come from the baseline towards the target; the latest value is the one for the latest day. A milestone: 100 % when reached, else 0 %. A goal: the mean of its key results that have a value; a key result with no value yet is left out and counted apart. An item is linked when it, an item above it or its project is linked to the goal, or to an active or done goal under it.',
  'rule.drift': 'The items done in the period up to today, in the projects you can see, and among them those that no goal with the status active or done is linked to, directly, through an item above it or through its project. Open and cancelled items, and items done outside the period, are not counted. With nothing done there is no share.',
  'dash.title': 'Dashboards',
  'dash.titleOf': 'Dashboard: {name} · {kind}',
  'dash.titleProject': 'Dashboard: {key} · {name}',
  'dash.title.organisation': 'The organisation',
  'dash.title.you': 'Your dashboard',
  'dash.intro': 'Fixed widgets for one layer: the work on both paths, the goals and drift for this quarter, and the fun figures of the last ended quarter. Each figure says what it counted.',
  'dash.introProject': 'The project\'s work, its goals and drift for this quarter, and the fun figures of its last closed sprint.',
  'dash.choose': 'Choose a dashboard',
  'dash.guest': 'As a guest you have your own dashboard; the layers and their goals are for the organisation\'s members.',
  'dash.work': 'The work',
  'dash.heading': 'Heading for {period}',
  'dash.toHeading': 'Open the Heading view',
  'dash.fun': 'Fun after the work, {quarter}',
  'dash.funTitle': 'Fun after the work',
  'dash.funSprint': 'Fun after the work, {sprint}',
  'dash.funNote': 'The figures of a closed period, as they were published; a group of fewer than {min} people shows no figure.',
  'dash.noSprint': 'No closed sprint yet: nothing to show.',
  'dash.children': 'The units under it',
  'dash.toLayers': 'Compare them side by side on Layers',
  'dash.ownedBy': 'Owned by {name}',
  'dash.countedProject': 'Counted: every open item, and every item done from {from} to {to}, in this project.',
  'dash.onlyYou': 'Only you see this.',
  'dash.yourWork': 'Your work',
  'dash.toMyWork': 'My work',
  'dash.fig.dueSoon': 'Due soon',
  'dash.dueSoon': '{n} items due within 7 days',
  'dash.dueSoon.one': '1 item due within 7 days',
  'dash.fig.done': 'Done by you in the period',
  'dash.yourWorkNote': 'The items you perform or own, in the projects you can see; done and on time: the items you performed, done from {from} to {to}.',
  'dash.yourGoals': 'The goals you own',
  'dash.noOwnGoals': 'You own no goal for this quarter.',
  'errors.GOAL_LEAD_ONLY': 'Goals of a unit are set by its leads, the leads above it and administrators.',
  'errors.GOAL_PARENT_UNIT': 'A goal can serve a goal of its own unit or a unit above it only.',
  'errors.GOAL_PARENT_PERIOD': 'A goal\'s period must lie within the period of the goal it serves.',
  'errors.GOAL_DROPPED': 'This goal is dropped: it takes no new key result, link or goal under it.',
  'errors.GOAL_FIXED': 'A goal\'s unit, the goal it serves and its period, and a key result\'s kind, are set when they are made.',
  'errors.GOAL_LINK_EXISTS': 'This is linked to the goal already.',
  'errors.HEADING_ROLE': 'Goals are for the organisation\'s members.',
  'errors.KR_SHAPE': 'A number needs a baseline and a different target; items done take a whole target or none; a milestone takes neither.',
  'errors.KR_ENTRY_KIND': 'A value is entered for a number, or reached / not reached for a milestone; items done are counted from the linked items.',
  'errors.KR_ENTRY_FUTURE': 'A value is entered for today or a day before.',

  // ── P4b: the planning views ────────────────────────────────────────────────────────────────────────────────
  'nav.plans': 'Plans',
  'plans.title': 'Plans: the timeline, the roadmap, workload and the calendar',
  'plans.intro': 'Read-only views of the work you can see. Every figure says what it counted.',
  'plans.choose': 'Choose a view',
  'plans.timeline': 'Timeline',
  'plans.roadmap': 'Roadmap',
  'plans.workload': 'Workload',
  'plans.calendar': 'Calendar',
  'plans.timelineAbout': 'A project\'s items from start to due date, what blocks what, the milestones and today.',
  'plans.roadmapAbout': 'Projects and epics by quarter, per owning unit, with the goals they serve.',
  'plans.workloadAbout': 'Your planned hours per week against your capacity; for leads, their people and team totals.',
  'plans.calendarAbout': 'Due dates, milestones and sprint days by month, and a calendar feed for your own calendar app.',
  'layers.builtTitle': 'Built since: the planning views, calibration and the full Learning view',
  'timeline.intro': 'Bars run from the start to the due date; a circle is a due date with no start; a diamond is a milestone.',
  'timeline.readOnly': 'Read-only: change dates on the card itself.',
  'timeline.project': 'Project',
  'timeline.noProjects': 'You see no project\'s items yet.',
  'timeline.nothing': 'No item of this project has a due date, so nothing is drawn.',
  'timeline.chartTitle': 'Timeline of {project}',
  'timeline.today': 'Today',
  'timeline.late': 'Late',
  'timeline.blocked': 'Blocked',
  'timeline.reversed': 'The start is after the due date',
  'timeline.legend.bar': 'From start to due date',
  'timeline.legend.mark': 'Due date only',
  'timeline.legend.milestone': 'Milestone',
  'timeline.legend.blocks': 'Blocks',
  'timeline.kind.bar': 'From start to due date',
  'timeline.kind.mark': 'Due date',
  'timeline.kind.milestone': 'Milestone',
  'timeline.col.item': 'Item',
  'timeline.col.kind': 'Drawn as',
  'timeline.col.start': 'Start',
  'timeline.col.due': 'Due',
  'timeline.col.state': 'State',
  'timeline.barWords': '{item}: from {from} to {to}',
  'timeline.markWords': '{item}: due {day}',
  'timeline.blocksWords': '{a} blocks {b}',
  'timeline.notDrawn': '{a} blocks {b}, which is not drawn (no due date)',
  'timeline.undated': '{n} items have no dates and are not drawn',
  'timeline.undated.one': '1 item has no dates and is not drawn',
  'timeline.outside': '{n} links lead to items of other projects and are not drawn',
  'timeline.outside.one': '1 link leads to an item of another project and is not drawn',
  'roadmap.intro': 'Projects span the quarters from their start to their target; epics from their start to their due date. Grouped by the unit that owns them.',
  'roadmap.prev': 'Year before',
  'roadmap.next': 'Year after',
  'roadmap.chartTitle': 'Roadmap {year}',
  'roadmap.nothing': 'No project you can see has dates in this year.',
  'roadmap.now': 'now',
  'roadmap.openEnded': 'no target yet',
  'roadmap.fromItems': 'from its items',
  'roadmap.noDates': 'No dates',
  'roadmap.done': 'done',
  'roadmap.restricted': 'restricted',
  'roadmap.col.work': 'Project or epic',
  'roadmap.col.quarters': 'Quarters',
  'roadmap.col.dates': 'Dates',
  'roadmap.col.goals': 'Goals it serves',
  'roadmap.undated': '{n} projects have no dates and are listed without quarters',
  'roadmap.undated.one': '1 project has no dates and is listed without quarters',
  'roadmap.noGoalsGuest': 'Goals are for the organisation\'s members, so none is shown here.',
  'workload.intro': 'Planned hours per week: the estimates of the open items a person performs, spread over the working days up to the due date, against their capacity.',
  'workload.onlyLeads': 'Planned hours per person are seen only by the person and the leads of their units. Never actual effort, never fun, never on time.',
  'workload.weeks': 'Weeks shown',
  'workload.weeksN': '{n} weeks',
  'workload.weeksN.one': '1 week',
  'workload.key': 'How full a week is',
  'workload.band.0': 'Nothing planned',
  'workload.band.1': 'Under half',
  'workload.band.2': 'Half to most',
  'workload.band.3': 'Nearly or exactly full',
  'workload.band.4': 'Over capacity',
  'workload.over': 'Over',
  'workload.cell': '{planned} / {capacity} h',
  'workload.cellWords': 'Week of {week}: {planned} hours planned of {capacity}',
  'workload.cellsLabel': 'Planned hours per week for {name}; the figures are in the table below.',
  'workload.thisWeek': 'This week',
  'workload.you': 'You',
  'workload.unitTitle': 'People and totals of {name}',
  'workload.noPeople': 'There is nobody else here whose planned load you may see. Leads see their people.',
  'workload.totals': 'Totals per unit',
  'workload.people': '{n} people',
  'workload.people.one': '1 person',
  'workload.small': 'Fewer than {min} people: no total is shown.',
  'workload.protect': 'Not shown, so that a smaller group beside it cannot be worked out.',
  'workload.later': 'Planned after the weeks shown: {hours} h',
  'workload.unscheduled': 'Not scheduled (no due date): {hours} h in {items} items',
  'workload.noEstimate': '{n} items have no estimate in hours',
  'workload.noEstimate.one': '1 item has no estimate in hours',
  'workload.lateCount': '{n} items are late: their hours are in this week',
  'workload.lateCount.one': '1 item is late: its hours are in this week',
  'workload.unseen': '{n} items in projects you cannot see: {hours} h, counted without their names',
  'workload.unseen.one': '1 item in a project you cannot see: {hours} h, counted without its name',
  'workload.defaultCapacity': 'Where no capacity is set, 32 hours a week is assumed (a 40-hour week less meetings).',
  'workload.whatCounts': 'What the hours are made of',
  'workload.noItems': 'No open item you can see.',
  'workload.noHours': 'no estimate in hours',
  'workload.hours': '{hours} h',
  'workload.setCapacity': 'Set the capacity',
  'workload.capacity': 'Capacity (hours per week)',
  'workload.capacityNote': 'Hours available for planned work. Do not write a reason: Pascal does not record why.',
  'workload.from': 'From',
  'workload.to': 'To',
  'workload.capacitySaved': 'The capacity is saved.',
  'workload.col.week': 'Week of',
  'workload.col.planned': 'Planned hours',
  'workload.col.capacity': 'Capacity',
  'workload.col.state': 'How full',
  'capacity.range': 'From {min} to {max} hours.',
  'calendar.intro': 'Due dates of open items, milestones and the first and last days of sprints.',
  'calendar.scope': 'Whose work',
  'calendar.mine': 'Mine',
  'calendar.all': 'Everything I can see',
  'calendar.prev': 'Month before',
  'calendar.next': 'Month after',
  'calendar.list': 'Everything this month',
  'calendar.none': 'Nothing is due this month in what is shown.',
  'calendar.more': '{n} more',
  'calendar.more.one': '1 more',
  'calendar.kind.due': 'Due',
  'calendar.kind.milestone': 'Milestone',
  'calendar.kind.sprintStart': 'Sprint starts',
  'calendar.kind.sprintEnd': 'Sprint ends',
  'calendar.short.due': 'Due',
  'calendar.short.milestone': 'Milestone',
  'calendar.short.sprintStart': 'Starts',
  'calendar.short.sprintEnd': 'Ends',
  'calendar.yearWheel': 'The year wheel comes with the recurring duties (planned).',
  'ics.title': 'Calendar feed (ICS)',
  'ics.intro': 'Your own due dates, milestones and sprint days in your calendar app, read-only. Only yours: nobody can make one for you.',
  'ics.privacy': 'Your calendar app, or the company behind it, fetches this address. Choose codes only to keep titles out of it. Restricted work never shows its code or title.',
  'ics.none': 'You have no calendar feed.',
  'ics.live': 'You have a feed since {day}: {detail}.',
  'ics.detail': 'What the events say',
  'ics.detail.codes': 'Item codes only',
  'ics.detail.titles': 'Codes and titles',
  'ics.make': 'Make a feed',
  'ics.replace': 'Make a new feed (the old address stops)',
  'ics.revoke': 'Revoke the feed',
  'ics.revoked': 'The feed is revoked: its address no longer works.',
  'ics.once': 'This address is shown once. Anyone who has it can read your feed: keep it to your own calendar app.',
  'ics.address': 'The feed\'s address',
  'rule.load': 'Planned hours: the current estimate in hours of each open item (backlog, ready, active, review) a person performs, divided among the people performing it, spread evenly over the working days from its start (or today) to its due date. A late item counts in this week; one without a due date is not scheduled; one without an estimate is counted apart. Capacity: the latest capacity set for the week, else 32 hours. Never actual effort, fun or on time.',
  'errors.CAPACITY_LEAD_ONLY': 'A person\'s capacity is set by the person or the leads of their units.',
  'errors.CAPACITY_DAYS': 'A capacity ends on or after the day it starts.',
  'errors.FEED_REVOKED': 'This calendar feed has been revoked.',
  'errors.ITEM_DATES': 'The start comes on or before the due date.',
  'errors.PROJECT_DATES': 'A project\'s target comes on or after its start.',
  'item.startOn': 'Start',
  'item.startHint': 'For the timeline: the day work is planned to begin.',
  'errors.LOAD_ROLE': 'Team totals are for the organisation\'s members, from the group size or for the team\'s leads.',
  // P4c: calibration and the full Learning view
  'learning.unitTitle': 'Calibration and trends by unit',
  'layers.plannedNone': 'Nothing of P4 is planned any more: every part is built.',
  'calib.title': 'Estimate calibration',
  'calib.yoursTitle': 'Your estimates',
  'calib.onlyYou': 'Only you see this. Nobody else can: not your lead, not an administrator.',
  'calib.window': 'Items done from {from} to {to}, with an estimate in hours frozen when work started and a rough actual.',
  'calib.notEnough': 'Not enough data yet: {n} of {min} items',
  'calib.notEnough.one': 'Not enough data yet: {n} of {min} items',
  'calib.counted': 'Calibration: {n} items with both estimate and actual',
  'calib.counted.one': 'Calibration: 1 item with both estimate and actual',
  'calib.withoutBoth': '{n} done items had no estimate in hours or no actual',
  'calib.withoutBoth.one': '1 done item had no estimate in hours or no actual',
  'calib.factor': 'Items took {factor} × the estimate',
  'calib.suggest': 'Suggested: multiply an estimate by {factor}. The raw estimate stays the record; you decide.',
  'calib.declined': 'Your estimates are as good as any correction we can compute.',
  'calib.declinedUnit': 'The estimates here are as good as any correction we can compute.',
  'calib.gainBetter': 'Tested on the newest {n} items: the correction would have brought the estimates {gain} % closer to what the work took. A suggestion needs at least {min} %.',
  'calib.gainBetter.one': 'Tested on the newest item: the correction would have brought the estimate {gain} % closer to what the work took. A suggestion needs at least {min} %.',
  'calib.gainWorse': 'Tested on the newest {n} items: the correction would have put the estimates {gain} % further off.',
  'calib.gainWorse.one': 'Tested on the newest item: the correction would have put the estimate {gain} % further off.',
  'calib.exact': 'The newest estimates were exact: there is nothing to correct.',
  'calib.measured': 'Measured: {measured} ×',
  'calib.towards': 'Drawn toward {unit}',
  'calib.towardsNone': 'Drawn toward no correction: no wider figure shows',
  'calib.unitsTitle': 'Calibration by unit',
  'calib.unitsIntro': 'The items done by people whose home team is in the unit, in the projects you can see. A unit shows only when at least {min} people did the counted work, so that nobody\'s own calibration can be read from it.',
  'calib.chartTitle': 'How long the work took against its estimate: {unit} and the units under it',
  'calib.noteSuggest': 'a correction is suggested',
  'calib.noteDeclined': 'no correction suggested',
  'calib.people': '{n} people did the counted work',
  'calib.people.one': '1 person did the counted work',
  'chart.scope': 'Unit',
  'chart.factor': 'Factor',
  'chart.note': 'Note',
  'chart.notShown': 'not shown',
  'chart.quarter': 'Quarter',
  'chart.axis': 'Axis',
  'chart.row': 'Type',
  'trends.title': 'Trends: the last four quarters',
  'trends.toDate': '{quarter} (to date)',
  'trends.intro': 'The work in the projects this unit or a unit under it owns, that you can see, over the last four quarters (this one so far).',
  'patterns.title': 'Problem patterns',
  'patterns.none': 'No problem was recorded in these quarters in the projects you can see.',
  'patterns.chartTitle': '{n} problems recorded, per quarter',
  'patterns.chartTitle.one': '1 problem recorded, per quarter',
  'patterns.problems': 'Problems',
  'patterns.recurring': 'Recurring causes: {list}',
  'patterns.noneRecurring': 'No cause came back more than once.',
  'patterns.total': 'In all',
  'patterns.notSaid': 'not said {n}',
  'patterns.notSaid.one': 'not said 1',
  'patterns.lessons': 'What to do next time, by kind of problem',
  'difficulty.title': 'Difficulty',
  'difficulty.chartTitle': 'The difficulty radar of done items, per quarter (0 none to 4 extreme)',
  'difficulty.notShown': 'not enough data',
  'difficulty.rated': '{n} rated items',
  'difficulty.rated.one': '1 rated item',
  'difficulty.notEnough': 'Not enough data: {n} of {min} rated items',
  'difficulty.notEnough.one': 'Not enough data: {n} of {min} rated items',
  'difficulty.type': 'Type',
  'difficulty.gapNotEnough': 'Before and after: not enough data ({n} of {min} items rated both times).',
  'difficulty.gapNotEnough.one': 'Before and after: not enough data ({n} of {min} items rated both times).',
  'difficulty.gapNone': 'Before and after agree on every axis ({n} items rated both times).',
  'difficulty.gapNone.one': 'Before and after agree on every axis (1 item rated both times).',
  'difficulty.gapUp': '{axis}: {gap} higher after the work than before, on average ({n} items)',
  'difficulty.gapUp.one': '{axis}: {gap} higher after the work than before (1 item)',
  'difficulty.gapDown': '{axis}: {gap} lower after the work than before, on average ({n} items)',
  'difficulty.gapDown.one': '{axis}: {gap} lower after the work than before (1 item)',
  'funTrend.title': 'How the work felt, per quarter',
  'funTrend.intro': 'Only quarters that have ended, and only groups of at least {min} people; the figures are published once and kept.',
  'funTrend.chartTitle': 'The mean answer before and after, for the whole unit (1 draining to 5 energising)',
  'funTrend.noGap': 'no gap to show',
  'aieffect.title': 'The effect of AI help',
  'aieffect.notCause': 'Comparison, not cause: items were not chosen at random for AI help.',
  'aieffect.none': 'No item was done in these quarters in the projects you can see.',
  'aieffect.chartTitle': 'Cycle time, the median in days, with and without AI help',
  'aieffect.with': 'With AI help',
  'aieffect.without': 'Without AI help',
  'aieffect.days': 'days',
  'aieffect.side': 'Help',
  'aieffect.n': 'Items',
  'aieffect.items': '{n} items',
  'aieffect.items.one': '1 item',
  'aieffect.notEnough': 'Not enough data to compare (at least {min} items each)',
  'aieffect.noCycle': 'no history to tell',
  'aieffect.cycle': '{days} days (median of {n})',
  'aieffect.cycle.one': '{days} days (1 item)',
  'aieffect.kOfN': '{k} of {n}',
  'aieffect.kOfN.one': '{k} of 1',
  'aieffect.noDue': 'none had a due date',
  'aieffect.cycleHead': 'Cycle time',
  'aieffect.ftrHead': 'First time right',
  'aieffect.ontimeHead': 'On time',
  'aieffect.achievedHead': 'Achieved',
  'aieffect.expertsTitle': 'Per AI expert: how the help was used',
  'aieffect.expert': 'AI expert',
  'aieffect.wrongHead': 'Problems: AI output wrong',
  'rule.calib': 'The items done in the last 12 months with an estimate in hours, frozen when work started, and a rough actual. For each, the actual divided by the estimate; the factor is their geometric mean, drawn toward the wider figure above (your team, the division, the organisation) with the weight n / (n + 10), or toward no correction when none shows. Fewer than 8 items: no factor at all. The test: the newest 30 % of the items are held out, the factor is worked out again on the older ones and applied to them; it is suggested only when it brings the typical (median) error at least 10 % closer. The raw estimate always stays the record. For a unit: the items its people performed, shown only with at least 5 people.',
  'rule.patterns': 'The problems found in the four quarters (by the day they were found), in the projects the unit or a unit under it owns that you can see, counted per quarter and kind. Time lost is counted as it was given, in its ranges, never turned into hours. A recurring cause is a kind with at least two problems; the three with the most are named. The lessons are the "next time" texts, newest first.',
  'rule.difficulty': 'For each done item, the radar after when there is one, else the radar before; the mean per axis over the items that rated it, per quarter done and per type. A quarter or type with fewer than 3 rated items shows no mean: an average of one or two items is that item. The gap is after minus before, over the items rated both times, from 3 of them.',
  'rule.aieffect': 'The items done in the four quarters, per type, in two groups: with AI help (an AI expert among the performers, or an AI contribution recorded) and without. For each: the median cycle time (cycle.v1), first time right (rework.v1), on time of those with a baseline due date (ontime.v1) and the outcome achieved. A group of fewer than 5 items shows no figure. It is a comparison, not a cause: nobody chose at random which items got AI help.',
  // ── P5: family links (files a person carries between the family's products) ────────────────────────────────
  'nav.family': 'Family',
  'family.title': 'Family links',
  'family.intro': 'Work the family proposes arrives as a file a person brings in. Nothing comes in by itself, and nothing becomes a card until a person accepts it.',
  'family.dataNote': 'Imported text is data: Pascal shows it and never follows it as an instruction.',
  'family.project': 'Project',
  'family.chooseProject': 'Choose a project',
  'family.noProjects': 'You see no project yet. A family file is brought into a project you work in.',
  'family.importTitle': 'Bring a file in',
  'family.file': "The file: pascal.workitem.v1 from a sibling, or Horizon's interchange file",
  'family.horizonHint': "From Horizon: while signed in to Horizon, download the interchange file for Pascal (contract 2), then choose it here. Pascal reads Horizon's public changes only.",
  'family.filterInstruments': 'Only these instruments (Horizon file, optional)',
  'family.filterJurisdictions': 'Only these jurisdictions (Horizon file, optional)',
  'family.filterHint': 'Codes separated by commas, for example AMLR, DORA.',
  'family.import': 'Import',
  'family.importing': 'Reading the file…',
  'family.cannotImport': "You can read this project's intake lane; a lead or member of the project brings files in and decides.",
  'family.report.title': 'What the file held',
  'family.report.file': 'From {product} ({instance}). File SHA-256: {sha}',
  'family.report.seen': '{n} items in the file',
  'family.report.seen.one': '1 item in the file',
  'family.report.proposed': '{n} new proposals',
  'family.report.proposed.one': '1 new proposal',
  'family.report.updates': '{n} updates of cards',
  'family.report.updates.one': '1 update of a card',
  'family.report.already': '{n} imported before, unchanged',
  'family.report.already.one': '1 imported before, unchanged',
  'family.report.superseded': '{n} older proposals replaced',
  'family.report.superseded.one': '1 older proposal replaced',
  'family.report.stale': '{n} older than the revision Pascal holds',
  'family.report.stale.one': '1 older than the revision Pascal holds',
  'family.report.otherProject': '{n} belong to another project',
  'family.report.otherProject.one': '1 belongs to another project',
  'family.report.skipped': '{n} skipped',
  'family.report.skipped.one': '1 skipped',
  'family.skip.malformed': 'not as the contract says',
  'family.skip.checksum_mismatch': 'its checksum does not match what it says',
  'family.skip.duplicate_in_file': 'the same reference twice in the file',
  'family.skip.filtered': 'outside the filter',
  'family.skip.over_cap': 'over 500 in one import',
  'family.lane.title': 'Intake lane',
  'family.lane.empty': 'No proposals wait here.',
  'family.lane.count': '{n} proposals wait for a decision',
  'family.lane.count.one': '1 proposal waits for a decision',
  'family.from': 'From {product}, {ref}, revision {revision}',
  'family.importedBy': 'Imported by {name} on {day}',
  'family.exportedBy': 'Exported by {name}, as the source names them',
  'family.update': 'Update of {code}',
  'family.newCard': 'A new card',
  'family.field.title': 'Title',
  'family.field.summary': 'Description',
  'family.field.due_on': 'Due',
  'family.field.due_kind': 'Due date kind',
  'family.changeLine': '{field}: {before} → {after}',
  'family.none': '(none)',
  'family.proposal.kind': 'Type: {kind}',
  'family.proposal.due': 'Due {day}',
  'family.proposal.noDue': 'No due date',
  'family.proposal.fourEyes': 'Four eyes',
  'family.accept': 'Accept as a card',
  'family.acceptUpdate': 'Accept the update',
  'family.accepted': 'Accepted as {code}',
  'family.reject': 'Reject',
  'family.rejectReason': 'Why not?',
  'family.rejectConfirm': 'Reject the proposal',
  'family.closedBy.source': 'Closed in {product}: Pascal sends its status back, and a person there decides.',
  'family.closedBy.pascal': 'Closed in Pascal.',
  'family.decided.title': 'Decided lately',
  'family.state.accepted': 'Accepted',
  'family.state.rejected': 'Rejected',
  'family.state.superseded': 'Replaced by a newer revision',
  'family.decidedBy': '{state} by {name}',
  'family.status.title': 'Status back to a product',
  'family.status.intro': "A pascal.status.v1 file of this project's cards that came from one product. It never closes anything there: a person in that product decides.",
  'family.status.product': 'Product',
  'family.status.export': 'Write the status file',
  'family.status.download': 'Download {file}',
  'family.status.written': '{n} cards in the file',
  'family.status.written.one': '1 card in the file',
  'family.origin.title': 'Origin',
  'family.origin.line': 'From {product}, {ref}, revision {revision}, imported by {name}',
  'family.origin.accepted': 'Accepted by {name}',
  'family.origin.open': 'Open in {product}',
  'errors.FILE_NOT_JSON': 'The file is not JSON. Choose the file the other product exported.',
  'errors.FILE_UNKNOWN_SCHEMA': "Pascal reads pascal.workitem.v1 and Horizon's interchange file (contract 2); this file is neither.",
  'errors.FILE_SHAPE': 'The file is not as its contract says. Nothing was imported.',
  'errors.FILE_HASH_MISMATCH': "The file's checksum does not match what it says: it was changed after it was exported. Nothing was imported.",
  'errors.FILE_TOO_MANY_ITEMS': 'A file holds at most {max} items. Ask for smaller files.',
  'errors.FILE_TOO_LARGE': 'The file is larger than {max} MB.',
  'errors.FILE_NOT_PUBLIC': "Pascal imports only Horizon's public feed; this file is of another class.",
  'errors.ENVELOPE_CONFLICT': '{ref} has changed at a revision Pascal already holds, so the file is refused. Ask for a file with a new revision.',
  'errors.ENVELOPE_DECIDED': 'This proposal has been decided already.',
  'errors.STATUS_NO_ITEMS': 'No card in this project came from that product.',
  // ── P6: AI experts, Ask Pascal, How Pascal works ───────────────────────────────────────────────────────────
  'nav.ask': 'Ask Pascal',
  'nav.how': 'How Pascal works',
  'nav.ai': 'AI',
  'ai.refusal.unknown_model': 'The model is not one Pascal may call.',
  'ai.refusal.engine_not_for_class': "This organisation's text goes only to Claude through the Anthropic API; this model is not allowed for it.",
  'ai.refusal.ai_not_allowed': 'AI is off for this organisation. An administrator turns it on under AI, after recording that the organisation accepts processing by Anthropic.',
  'ai.refusal.no_engine_for_class': 'No AI engine for this kind of data is set up on this server. Ask whoever runs it.',
  'ai.refusal.boundary_unchecked': "Pascal could not check the organisation's AI allowance just before sending, so nothing was sent.",
  'ai.off.allowance': 'AI is off for this organisation until an administrator turns it on.',
  'ai.off.engine': 'No AI engine is set up on this server, so AI cannot run here.',
  'ai.preview.title': 'Before anything is sent',
  'ai.preview.intro': 'This is what would go to the model. Nothing is sent until you press Send. The answer comes back as a proposal: nothing on the board changes until a person accepts it.',
  'ai.preview.model': 'Model',
  'ai.preview.engine': 'Engine',
  'ai.preview.class': 'Data class',
  'ai.preview.worstCase': 'Most it can cost',
  'ai.preview.wouldRefuse': 'This would be refused: {reason}',
  'ai.preview.prompt': 'The prompt, as it would be sent',
  'ai.preview.send': 'Send',
  'ai.engine.anthropic': 'Anthropic API (Claude)',
  'ai.engine.openrouter': 'OpenRouter, pinned to Inceptron (EU, zero retention)',
  'ai.class.client': "Client: the organisation's own text",
  'ai.class.synthetic': 'Synthetic: a declared fictional organisation',
  'ai.class.public': 'Public: text shipped with Pascal',
  'ai.whatWentIn.open': 'What went in',
  'ai.call.status': 'Outcome',
  'ai.call.status.ok': 'Answered',
  'ai.call.status.error': 'Failed',
  'ai.call.status.refused': 'Refused before sending: nothing left Pascal',
  'ai.call.status.cancelled': 'Cancelled',
  'ai.call.why': 'Why',
  'ai.call.nothingSent': 'None: nothing was sent',
  'ai.call.cost': 'Cost',
  'ai.call.when': 'When',
  'ai.call.partsIntro': "Each part, in the order it was composed. Pascal's own templates are shown as text; the organisation's text is kept only as a fingerprint of what was shown, with where it lives.",
  'ai.part.date_block': 'The date',
  'ai.part.fence_rules': 'How to read what is given',
  'ai.part.preamble': "The task's preamble",
  'ai.part.instructions': 'The instructions',
  'ai.part.nudge': 'The retry: answer in JSON only',
  'ai.part.public_input': 'A help section shipped with Pascal',
  'ai.part.work_text': "The organisation's text, inside a fence",
  'ai.ref.item': 'A card, as it read then (fingerprint {sha}…)',
  'ai.ref.sprint': "A sprint's problems and lessons (fingerprint {sha}…)",
  'ai.ref.board': "A board's teams, labels, types, sprints and epics (fingerprint {sha}…)",
  'ai.ref.question': 'Your question, kept only as a fingerprint ({sha}…)',
  'ai.proposal.count': '{n} proposals',
  'ai.proposal.count.one': '1 proposal',
  'ai.proposal.kind.breakdown': 'Proposed subtasks',
  'ai.proposal.kind.draft': 'Proposed draft of this card',
  'ai.proposal.kind.retro': 'Draft summary',
  'ai.proposal.state.proposed': 'waiting for a person',
  'ai.proposal.state.accepted': 'accepted',
  'ai.proposal.state.rejected': 'rejected',
  'ai.proposal.by': 'asked for by {name}, {when}',
  'ai.proposal.choose': 'Choose the subtasks to make',
  'ai.proposal.made': 'Cards made: {codes}',
  'ai.proposal.decidedBy': 'Decided by {name}',
  'ai.proposal.reason': 'Why: {reason}',
  'ai.proposal.acceptChosen': 'Make the chosen subtasks',
  'ai.proposal.acceptDraft': 'Use this title and description',
  'ai.proposal.keep': 'Keep as our draft',
  'ai.proposal.reject': 'Reject',
  'ai.proposal.why': 'Why it is rejected',
  'ai.proposal.rejectSend': 'Reject the proposal',
  'ai.item.title': 'AI on this card',
  'ai.item.breakdown': 'Break down with AI',
  'ai.item.draft': 'Draft with AI',
  'ai.item.proposeNote': 'AI proposes; you decide. Nobody is assigned and no date or estimate is set.',
  'ai.openIn': 'Open in {product}',
  'ai.openIn.note': "The link opens the product's own screen with this card's code. Nothing is sent; you do the work there, under its own sign-in and rules.",
  'ai.retro.title': 'Draft summary by AI',
  'ai.retro.intro': "A model may draft a summary of this sprint's problems and lessons. It is a draft, it names nobody and holds no figure: every count on this page is the board's own.",
  'ai.retro.draft': 'Draft a summary with AI',
  'ai.retro.draftLabel': 'Draft written by a model, from the problems and lessons as typed',
  'ask.title': 'Ask Pascal',
  'ask.intro': 'Ask how something works, or find cards on a board. Answers about the work are counted by the board, never by a model.',
  'ask.modes': 'What to ask',
  'ask.mode.help': 'How do I…?',
  'ask.mode.find': 'Find…',
  'ask.help.question': 'Your question',
  'ask.help.hint': 'Pascal searches its own help. Nothing is sent anywhere.',
  'ask.help.search': 'Search the help',
  'ask.help.none': 'The help has no section on that. Try other words, or open How Pascal works.',
  'ask.help.answer': "Pascal's answer",
  'ask.help.answerNote': 'Written by a model from the help sections below, and only from them.',
  'ask.help.cites': 'Sections used: {ids}',
  'ask.help.section': 'Help section {id}',
  'ask.help.sheet': 'See it on its sheet',
  'ask.help.askModel': 'Ask Pascal to answer in a few sentences',
  'ask.find.intro': 'Pascal turns your question into a filter for one board; the board counts the cards, with the same rule as its list view. A filter never names a person.',
  'ask.find.guest': 'Finding with AI is for members of the organisation.',
  'ask.find.noBoards': 'You see no board yet.',
  'ask.find.board': 'Board',
  'ask.find.question': 'What are you looking for?',
  'ask.find.hint': 'For example: what is late in the KYC team?',
  'ask.find.ask': 'Find',
  'ask.find.result': 'The filter Pascal proposes',
  'ask.find.count': '{n} of the {total} cards on this board match',
  'ask.find.count.one': '1 of the {total} cards on this board matches',
  'ask.find.rule': "Counted by the board with {rule}, the list view's own rule.",
  'ask.find.late': 'Late',
  'ask.find.mode': 'Done by: {mode}',
  'ask.find.type': 'Type: {type}',
  'ask.find.label': 'Label: {label}',
  'ask.find.words': 'Words: {q}',
  'ask.find.unit': 'People of {unit}',
  'ask.find.epic': 'Under the epic {epic}',
  'ask.find.sprint': 'In {sprint}',
  'ask.find.noSprint': 'In no sprint',
  'ask.find.open': 'Open the board',
  'ask.find.save': 'Save as my filter',
  'ask.find.saved': 'Saved. It is among your filters on the board.',
  'how.title': 'How Pascal works',
  'how.intro': 'Six sheets: click a box to see what it is, where it lives and which tables hold it, or step through a sheet. Nothing here counts anything: the screens do.',
  'how.print': 'Download for print (A3)',
  'how.sheets': 'Sheets',
  'how.stepStart': 'Step through',
  'how.stepBack': 'Previous',
  'how.stepNext': 'Next',
  'how.stepOf': 'Step {i} of {total}',
  'how.where': 'Where:',
  'how.tables': 'Tables:',
  'ai.settings.title': 'AI in this organisation',
  'ai.settings.intro': "Pascal's own AI breaks epics down, drafts intake cards, drafts retrospective summaries and answers Ask Pascal. It only ever proposes; a person decides. It never reads a person's history, ratings or workload.",
  'ai.settings.state': 'Where AI stands',
  'ai.settings.allowance': 'AI allowance',
  'ai.settings.on': 'On',
  'ai.settings.off': 'Off',
  'ai.settings.notNeeded': 'Not needed: a declared fictional organisation',
  'ai.settings.engine': 'Engine on this server',
  'ai.settings.engineReady': 'Set up',
  'ai.settings.engineMissing': 'Not set up (ask whoever runs the server)',
  'ai.settings.available': 'AI can run now',
  'ai.settings.yes': 'Yes',
  'ai.settings.no': 'No',
  'ai.settings.caps': 'Budget',
  'ai.settings.capOrgDay': 'Organisation, per day',
  'ai.settings.capPersonDay': 'Each person, per day',
  'ai.settings.capOrgMonth': 'Organisation, per month',
  'ai.settings.noCap': 'No cap',
  'ai.settings.spentMine': 'You today',
  'ai.settings.spentOrgDay': 'The organisation today',
  'ai.settings.spentOrgMonth': 'The organisation this month',
  'ai.settings.capsNote': "Each call reserves the most it can cost before it is sent; a call that would pass a cap is refused. Days and months are UTC. Nobody sees another person's spend.",
  'ai.settings.turnOn': 'Turn AI on',
  'ai.settings.turnOff': 'Turn AI off',
  'ai.settings.onIntro': "Turning AI on sends the organisation's text, when a person asks, to Claude through the Anthropic API. Record that the organisation accepts that processing, and on what basis.",
  'ai.settings.offIntro': 'Turning AI off stops every call at once, also one waiting to be sent. Say why.',
  'ai.settings.statement': 'What the organisation accepts',
  'ai.settings.why': 'Why',
  'ai.settings.history': 'Changes',
  'ai.settings.turnedOn': 'Turned on by {name}, {when}',
  'ai.settings.turnedOff': 'Turned off by {name}, {when}',
  'errors.AI_REFUSED': 'This was not sent to a model. Nothing left Pascal.',
  'errors.AI_NOT_CONFIGURED': 'No AI engine is set up on this server.',
  'errors.DAILY_SPEND_CAP': "Today's AI budget is used up. It opens again tomorrow (UTC).",
  'errors.MONTHLY_SPEND_CAP': "This month's AI budget is used up.",
  'errors.MODEL_REPLY_UNREADABLE': "The model's answer could not be used, so nothing was proposed. Try again.",
  'errors.MODEL_UNAVAILABLE': 'The model could not answer just now. Try again later.',
  'errors.AI_TARGET': 'AI cannot do that here: break down an epic, or draft a card in the intake lane.',
  'errors.AI_ROLE': 'This needs a member or an administrator of the organisation.',
  'errors.PROPOSAL_DECIDED': 'This proposal has been decided already.',
  'errors.ALLOWANCE_STATEMENT': 'Write what the organisation accepts, or why AI is turned off (at least 10 characters).',
  'how.onion.title': 'The onion',
  'how.onion.intro': 'An organisation, its divisions and teams, and two ways its work adds up.',
  'how.onion.organisation.title': 'The organisation',
  'how.onion.organisation.what': "The outer layer. Every row in Pascal belongs to one organisation, and nobody sees another organisation's rows.",
  'how.onion.division.title': 'Divisions',
  'how.onion.division.what': 'The layer under the organisation. A division has leads and members, and owns projects.',
  'how.onion.team.title': 'Teams',
  'how.onion.team.what': 'The layer under a division. A person has one home team and may belong to others.',
  'how.onion.peoplePath.title': 'Done by people here',
  'how.onion.peoplePath.what': 'The first way up: the cards the people whose home is in this unit performed, wherever the project lives. Shown only for at least five people.',
  'how.onion.projectPath.title': 'Work in projects owned here',
  'how.onion.projectPath.what': 'The second way up: the cards of the projects this unit owns, whoever did them. Projects you cannot see are counted, never named.',
  'how.item-life.title': 'The life of a card',
  'how.item-life.intro': 'From a request to done, with the gates a person must pass.',
  'how.item-life.intake.title': 'Intake',
  'how.item-life.intake.what': "Requests arrive here, typed in or brought in from the family. Pascal's AI may draft a clearer card; a person decides.",
  'how.item-life.backlog.title': 'Backlog',
  'how.item-life.backlog.what': 'Ranked work waiting to start. An epic can be broken down into proposed subtasks.',
  'how.item-life.active.title': 'In progress',
  'how.item-life.active.what': 'Work under way. The first estimate is frozen as the baseline when the card first gets here.',
  'how.item-life.review.title': 'Review',
  'how.item-life.review.what': 'Someone looks at the work before it is done.',
  'how.item-life.done.title': 'Done',
  'how.item-life.done.what': 'Done needs an outcome and a person who confirms it: the one signed in. An AI expert never confirms.',
  'how.item-life.fourEyes.title': 'Four eyes',
  'how.item-life.fourEyes.what': 'A control task needs a second person, neither a performer nor the confirmer. The database enforces it.',
  'how.who-did.title': 'Who did the work',
  'how.who-did.intro': 'A person, an AI expert of the family, or a pair; and what an AI contributed.',
  'how.who-did.person.title': 'A person',
  'how.who-did.person.what': 'Performs the card. Their name is on the work record; how it felt to them stays theirs.',
  'how.who-did.aiExpert.title': 'An AI expert',
  'how.who-did.aiExpert.what': 'ANTON, Valentin, Gabriel and the others can perform a card. They propose; they never own, confirm or check it.',
  'how.who-did.pair.title': 'A pair',
  'how.who-did.pair.what': 'A person and an AI expert together. The Learning view compares how pairs went, as a comparison and not a cause.',
  'how.who-did.receipt.title': 'The contribution receipt',
  'how.who-did.receipt.what': 'A person does AI work in the sibling product itself, opened from the card, and records what went in and what it cost.',
  'how.who-did.ownAi.title': "Pascal's own AI",
  'how.who-did.ownAi.what': 'Breaks epics down, drafts cards and summaries, answers Ask Pascal. Every answer is a proposal, with a preview before and a record after.',
  'how.learning-loop.title': 'The learning loop',
  'how.learning-loop.intro': 'Estimate, actual, calibration, problems and the retrospective.',
  'how.learning-loop.baseline.title': 'The baseline',
  'how.learning-loop.baseline.what': 'The first estimate, frozen when work starts. A new estimate is a new figure beside it, with a reason.',
  'how.learning-loop.actual.title': 'The actual',
  'how.learning-loop.actual.what': 'How long it really took, in coarse steps. It is not a timesheet.',
  'how.learning-loop.calibration.title': 'Calibration',
  'how.learning-loop.calibration.what': "Code compares estimates and actuals. Below eight cards it says nothing; you see your own suggestion, nobody else's.",
  'how.learning-loop.problems.title': 'Problems',
  'how.learning-loop.problems.what': 'Blame-free: what went wrong, how it was found and solved, what to try next time.',
  'how.learning-loop.retro.title': 'The retrospective',
  'how.learning-loop.retro.what': "Where the learning is discussed. A model may draft a summary from the texts; the counts beside it are the board's.",
  'how.family.title': 'The family',
  'how.family.intro': 'Files in, status out, and who owns what.',
  'how.family.fileIn.title': 'A file in',
  'how.family.fileIn.what': 'Another product of the family exports a file; a person brings it in. Nothing arrives by itself.',
  'how.family.intakeLane.title': 'The intake lane',
  'how.family.intakeLane.what': 'Each item becomes a proposal. Its text is data, never instructions.',
  'how.family.accept.title': 'Accept',
  'how.family.accept.what': 'A lead or member of the project makes it a card, or rejects it with a reason.',
  'how.family.statusOut.title': 'Status out',
  'how.family.statusOut.what': 'Pascal writes a status file to carry back. It never closes anything in the other product.',
  'how.family.owners.title': 'Who owns what',
  'how.family.owners.what': 'The source owns its record and its closure; Pascal owns the planning.',
  'how.your-data.title': 'Your data',
  'how.your-data.intro': 'What is yours alone, what is shown only in groups of five, and what a model is given.',
  'how.your-data.ownRatings.title': 'Yours alone',
  'how.your-data.ownRatings.what': "How a card felt to you is seen by you only, whatever anyone's role.",
  'how.your-data.groupSize.title': 'Groups of five',
  'how.your-data.groupSize.what': 'A group figure needs five different people and a period that has ended; what subtraction would expose is hidden.',
  'how.your-data.noRanking.title': 'No ranking',
  'how.your-data.noRanking.what': "No view ranks people and there is no performance score. No model proposes who should do a card or reads a person's history.",
  'how.your-data.aiRecords.title': 'What a model is given',
  'how.your-data.aiRecords.what': 'Only what the task needs, fenced as data. Pascal keeps a fingerprint of your text, never the text, and you can see what went in.',
  'how.your-data.erase.title': 'Erase',
  'how.your-data.erase.what': 'You can erase your own answers; the group figures they counted in are worked out again without them.',

  // ── P7: privacy pack, leavers, recurring work, templates, intake forms, import ─────────────────────────────
  'nav.privacyPack': 'Privacy pack',
  'nav.wheel': 'Annual cycle',
  'nav.import': 'Import',
  'nav.intake': 'Ask a team',
  'p7.project': 'Project',
  'p7.board': 'Board',
  'p7.noProjects': 'You see no project yet.',
  'pack.title': 'Privacy pack',
  'pack.intro': 'Your own data to download, and the documents an employer completes before Pascal is used with real employees.',
  'pack.draft': 'DRAFT: needs legal review. Not legal advice.',
  'pack.read': 'Read the document',
  'pack.download': 'Download {file}',
  'pack.lang.en': 'In English',
  'pack.lang.sv': 'In Swedish',
  'mydata.title': 'Your data',
  'mydata.intro': 'Everything Pascal holds about you: your account, roles and teams, the cards you perform, report or own, your comments and problems, your own answers, your planned hours and your AI calls. Only yours, whatever your role.',
  'mydata.download': 'Download my data',
  'mydata.downloadCsv': 'Download as CSV',
  'mydata.withheld': 'A card in a project you can no longer see is listed by its code, without its title. Ask your organisation if you need more.',
  'leaver.title': 'Leaving',
  'leaver.intro': 'The account is switched off on the leaving day. Their answers are deleted 30 days later. Their name stays on their work until you replace it with a pseudonym, which cannot be undone.',
  'leaver.person': 'Who',
  'leaver.day': 'Leaving day',
  'leaver.reason': 'Why',
  'leaver.record': 'Record leaving',
  'leaver.recorded': 'Leaving recorded.',
  'leaver.pseudonymise': 'Replace the name with a pseudonym',
  'leaver.pseudonymiseConfirm': 'Replace the name of {name} with a pseudonym? This cannot be undone.',
  'leaver.pseudonymised': 'The name has been replaced.',
  'leaver.leftOn': 'left on {day}',
  'wheel.title': 'Annual cycle',
  'wheel.intro': 'The recurring duties of the year, month by month: the cards already made and the days still to come.',
  'wheel.previous': 'Previous year',
  'wheel.next': 'Next year',
  'wheel.yearTitle': 'The year {year}',
  'wheel.counts': 'Cards made: {cards}. Still to come: {toCome}.',
  'wheel.figure': 'The annual cycle of {year}: the number of recurring duties in each month',
  'wheel.tableCaption': 'Recurring duties in {year}, by month',
  'wheel.month': 'Month',
  'wheel.duties': 'Duties',
  'wheel.none': 'None',
  'wheel.card': 'card {code}',
  'wheel.toCome': 'to come',
  'series.title': 'Recurring work',
  'series.none': 'This project has no recurring work yet.',
  'series.cards': '{n} cards',
  'series.cards.one': '1 card',
  'series.from': 'from {day}',
  'series.status.active': 'Active',
  'series.status.paused': 'Paused',
  'series.status.retired': 'Retired',
  'series.pause': 'Pause',
  'series.resume': 'Resume',
  'series.retire': 'Retire',
  'series.retireConfirm': 'Retire "{title}"? It makes no new cards and cannot be resumed; its cards stay.',
  'series.leadOnly': 'The project lead sets up recurring work.',
  'series.new': 'New recurring work',
  'series.field.title': 'Title of each card',
  'series.field.basis': 'Why it exists',
  'series.field.basisHint': 'A rule, a policy or a contract; shown on every card.',
  'series.field.type': 'Kind of card',
  'series.field.rule': 'How often',
  'series.field.startsOn': 'First day',
  'series.field.roll': 'On a weekend',
  'series.field.prepDays': 'Days to prepare (the card starts this many days before it is due)',
  'series.type.duty': 'Recurring duty',
  'series.type.control': 'Control (four eyes)',
  'series.type.task': 'Task',
  'series.type.remediation': 'Remediation (four eyes)',
  'series.type.milestone': 'Milestone',
  'series.rule.monthly15': 'Every month on the 15th',
  'series.rule.monthlyFirst': 'Every month on the 1st',
  'series.rule.monthlyLastFriday': 'Every month on the last Friday',
  'series.rule.quarterly': 'Every quarter on the 1st',
  'series.rule.yearly': 'Every year on the first day',
  'series.rule.weeklyMonday': 'Every Monday',
  'series.roll.next_business_day': 'Move to the next weekday',
  'series.roll.previous_business_day': 'Move to the weekday before',
  'series.roll.none': 'Keep the day',
  'series.save': 'Set up',
  'series.made': '{n} cards made for the next 12 months.',
  'series.made.one': '1 card made for the next 12 months.',
  'import.title': 'Import',
  'import.intro': 'Bring cards in from a CSV file: check it first, nothing is saved; then import it once. Every card lands open in the intake lane.',
  'import.noProjects': 'You write no project yet, so there is nothing to import into.',
  'import.csvTitle': 'A CSV file',
  'import.preset': 'Where the file comes from',
  'import.preset.jira': 'Jira (CSV export)',
  'import.preset.monday': 'Monday (CSV saved from its Excel export)',
  'import.preset.pascal': 'Pascal\'s own columns',
  'import.presetHint.jira': 'Summary, Issue Type, Status, Priority, Description, Due date, Labels, Original Estimate, Parent id.',
  'import.presetHint.monday': 'Name, Status, Priority, Date, Notes; each group becomes a label.',
  'import.presetHint.pascal': 'title, description, type, priority, due_on, estimate_hours, labels (separated by ;).',
  'import.file': 'File',
  'import.rules': 'Cards closed in the source are not imported. People are never imported: assign them in Pascal.',
  'import.dryRun': 'Check first (nothing is saved)',
  'import.run': 'Import',
  'import.created': '{n} cards imported.',
  'import.created.one': '1 card imported.',
  'import.report.title': 'What the file holds',
  'import.report.rows': '{n} rows read',
  'import.report.rows.one': '1 row read',
  'import.report.toCreate': '{n} cards to create',
  'import.report.toCreate.one': '1 card to create',
  'import.report.closed': '{n} closed in the source: not imported',
  'import.report.closed.one': '1 closed in the source: not imported',
  'import.report.refused': '{n} rows refused',
  'import.report.refused.one': '1 row refused',
  'import.report.unknownTypes': '{n} of an unknown type, imported as tasks',
  'import.report.unknownTypes.one': '1 of an unknown type, imported as a task',
  'import.report.unreadDates': '{n} dates could not be read and are left empty',
  'import.report.unreadDates.one': '1 date could not be read and is left empty',
  'import.report.groups': '{n} groups',
  'import.report.groups.one': '1 group',
  'import.report.refusedRow': 'Row {row}: {reason}',
  'import.reason.no_title': 'no title',
  'import.reason.title_too_long': 'the title is longer than 200 characters',
  'import.report.ignored': 'Not read: {columns}',
  'import.report.nothingSaved': 'Nothing has been saved.',
  'template.title': 'Templates',
  'template.choose': 'Template',
  'template.kind.project': 'a project plan',
  'template.kind.checklist': 'a checklist under a card',
  'template.items': '{n} cards',
  'template.items.one': '1 card',
  'template.under': 'Under the card',
  'template.pickCard': 'Choose a card',
  'template.startOn': 'Start day',
  'template.startOnHint': 'Optional: the due dates count from it.',
  'template.apply': 'Use this template',
  'template.made': '{n} cards made.',
  'template.made.one': '1 card made.',
  'intake.title': 'Ask a team',
  'intake.intro': 'Send a request to a team without e-mail. It arrives in their intake lane, and you can follow it here.',
  'intake.fcpLine': 'Do not name customers. Use the case reference from your case system.',
  'intake.noForms': 'No team takes requests from you here yet.',
  'intake.field.title': 'What do you need?',
  'intake.field.description': 'Details',
  'intake.field.dueOn': 'Needed by',
  'intake.field.priority': 'Priority',
  'intake.send': 'Send the request',
  'intake.sent': 'Sent as {code}.',
  'intake.mine': 'Your requests',
  'intake.mineNone': 'You have sent no request.',
  'intake.manage': 'Forms of the projects you lead',
  'intake.forms': 'Forms',
  'intake.formsNone': 'This project has no form yet.',
  'intake.status.open': 'Open',
  'intake.status.closed': 'Closed',
  'intake.open': 'Open it',
  'intake.close': 'Close it',
  'intake.who.organisation': 'Anyone in the organisation',
  'intake.who.members': 'The project\'s members only',
  'intake.newForm': 'New form',
  'intake.formTitle': 'Name of the form',
  'intake.formIntro': 'A line for the person asking',
  'intake.whoField': 'Who may send',
  'intake.fieldsAsked': 'Also ask',
  'intake.fieldName.description': 'details',
  'intake.fieldName.due_on': 'needed by',
  'intake.fieldName.priority': 'priority',
  'intake.createForm': 'Set up the form',
  'errors.LEAVER_OWN': 'Nobody records their own leaving or pseudonym.',
  'errors.LEAVER_DAY': 'The leaving day is at most a year ahead.',
  'errors.LEAVER_REASON': 'Say why, in at least 3 characters.',
  'errors.PSEUDONYMISE_NOT_LEFT': 'Only a person who has left can be given a pseudonym.',
  'errors.PSEUDONYMISED': 'This person has a pseudonym already.',
  'errors.MYDATA_NO_PARAMETERS': 'Your data is always your own: this takes no parameter.',
  'errors.SERIES_RULE': 'Pascal cannot read this rule. Choose a weekly, monthly or yearly rule.',
  'errors.SERIES_TOO_OFTEN': 'A recurring card comes at most once a week.',
  'errors.SERIES_RETIRED': 'This recurring work has been retired.',
  'errors.SERIES_STARTS': 'The first day is within ten years.',
  'errors.TEMPLATE_UNKNOWN': 'There is no such template.',
  'errors.TEMPLATE_LOCK': 'This template has changed since it was checked, so it is not used.',
  'errors.TEMPLATE_KIND': 'A checklist goes under a card; a project plan goes on a board.',
  'errors.INTAKE_CLOSED': 'This form is closed.',
  'errors.INTAKE_NOT_FOR_YOU': 'This form is for the project\'s members.',
  'errors.IMPORT_CSV': 'The file cannot be read as CSV: a quoted field is never closed.',
  'errors.IMPORT_EMPTY': 'Nothing in this file would become a card.',
  'errors.IMPORT_TOO_MANY': 'At most 2,000 rows and 5 MB at a time.',
  'errors.IMPORT_DUPLICATE': 'This file has been imported into this project already.',
} as const

export type MessageKey = keyof typeof en
